import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  AuthService,
  VaultAppToastService,
  VaultOperativeRoleSyncService,
  syncRoleIdsIntoUserData,
  userCanAssignRepositoryFilesFromSession,
  userIsAdminEmpresaFromSession,
  userIsSuperadminFromSession,
  vaultCurrentUserIdFromSession,
  vaultCurrentTenantIdFromSession,
  readPersistedVaultUserId,
  VAULT_ADMIN_USERS_CHANGED_EVENT,
  VAULT_FILE_ASSIGNMENTS_CHANGED_EVENT,
  notifyVaultFileAssignmentsChanged,
  readVaultItemsPerPageFromPreferences,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
} from '@nebula/shared-vault/data-access';
import type { FileNode } from '@nebula-shared/ui';
import { openVaultConfirmDialog } from '@nebula-shared/ui';
import {
  ActionEvent,
  ButtonComponent,
  CheckboxComponent,
  BadgeComponent,
  DialogService,
  EmptyComponent,
  IconComponent,
  InputComponent,
  SelectComponent,
  TableComponent,
  type SortChangeEvent,
  type TableConfig,
} from 'nebula-ui-kit';
import {
  FieldLabelComponent,
  FileTreeComponent,
  applyVaultModuleInitialLoadErrorSignals,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  VaultModuleShellComponent,
  VaultPageAlertComponent,
  type PageHeaderAction,
  type VirtualFolderPreview,
} from '@nebula-shared/ui';
import { FileAssignmentService } from '../../../../data-access/src/lib/repository/file-assignment.service';
import { ProfileService } from '../../../../data-access/src/lib/profile/profile.service';
import { RepositoryService } from '../../../../data-access/src/lib/repository/repository.service';
import { VaultTenantScopeService } from '../../../../data-access/src/lib/shared/vault-tenant-scope.service';
import {
  firstNonEmptyTenantId,
  persistActiveTenantId,
  syncVaultTenantScopeFromSession,
  tenantIdFromAssignUsers,
  tenantIdFromAuthMeResponse,
  tenantIdFromMyTenantsResponse,
} from '../../../../data-access/src/lib/shared/tenant-context.util';
import type {
  AssignableAdminUser,
  FileAssignmentRecord,
} from '../../../../domain/src/lib/repository/file-assignment.model';
import type { RepositoryListItem } from '../../../../domain/src/lib/repository/repository.model';
import { ServerContextService } from '../servers/server-context.service';
import { catchError, EMPTY, finalize, forkJoin, from, Observable, of, take, timeout, toArray } from 'rxjs';
import { concatMap, filter, first, map, switchMap } from 'rxjs/operators';
import { VaultFilePreviewComponent } from './vault-file-preview.component';
import { repositoryAdminStore } from '../../../../store/src/lib/repository-admin.store';
import {
  detectMimeFromName,
  isOfficeMime,
  isPdfMime,
  isTextLikeMime,
} from './repository-preview-mime.util';
import {
  type AssignTargetItem,
  buildAssignTargetItems,
  buildRepositoryPathPrefixes,
  buildZipVirtualAssignmentPath,
  filterOaisAssignableAssignments,
  clampRepositoryBrowsePath,
  formatRepositoryContextPath,
  getOaisAssignmentBlockReason,
  getParentRepositoryExplorerPath,
  getParentZipInternalPath,
  isDipAssignablePath,
  isAipFolderPath,
  isSipFolderPath,
  isOutsideRepositoryBrowseRoot,
  isUnderDipSegment,
  normalizeRepositoryExplorerPath,
  normalizeZipInternalPath,
  REPOSITORY_EXPLORER_ROOT_PATH,
  resolveAssignIsDirectoryFlag,
  resolveRepositoryDeepLinkListPath,
  resolveRepositoryItemPath,
  resolveRepositorySearchHitPath,
  resolveZipInternalNavigatePath,
  resolveZipInternalRowSegment,
  extractZipInternalRelativePath,
  resolveOaisZipEmptyFolderFallback,
  sanitizeZipInternalPathForList,
  splitZipVirtualAssignmentPath,
} from './repository-assignment-path.util';
import {
  allOaisFolderProbeNames,
  classifyOaisFolderName,
  dedupeOaisPackagePaths,
  isOaisRootManifestFileName,
  listingHasOaisMetaOrManifiesto,
  listingLooksLikeOaisPackageContainer,
  mergeOaisListingItems,
  packOaisZipInternalParts,
  rebaseZipToPackageRoot,
  resolveMissingOaisRootManifestFileProbes,
  resolveMissingOaisSiblingProbes,
  resolveNestedOaisManifestProbeFolders,
  resolveOaisPackageRootPath,
  resolveSipFolderFromOaisZipPath,
  resolveZipCollectItemPath,
  shouldProbeOaisRootManifest,
  zipBlobHasCompleteOaisLayout,
} from './repository-package-download.util';
import {
  canRepositoryTableNavBack,
  resolveRepositoryFolderGoUp,
  resolveRepositoryZipGoUp,
} from './repository-table-nav.util';
import {
  collectZipArchivePathsFromListing,
  repositorySearchResponseIsEmpty,
  searchInsideZipArchives$,
  zipClientSearchHitsAsListItems,
} from './repository-zip-client-search.util';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../vault-table-pagination-bind.util';
import {
  assignUserCheckboxLabel as buildAssignUserCheckboxLabel,
  formatAssignRoleLabel,
  mapUsersForAssignDialog,
} from './repository-assign-users.util';
import {
  groupFileAssignments,
  type RepositoryAssignmentGroup,
} from './repository-assignment-groups.util';
import {
  canRepositoryTableNavBackScoped,
  filterRepositoryItemsByMyAssignments,
  hasFullRepositoryBrowseAccess,
  isAssignmentDirectoryRecord,
  isRepositoryNavigationPathAllowed,
  isRepositoryZipContextAllowed,
  resolveAssignmentScopedHomePath,
  resolveAssignmentListingPathCorrection,
  resolveMyFileAssignmentsFromApi,
  resolveRepositoryFolderGoUpScoped,
  resolveRepositoryZipGoUpScoped,
  resolveUniqueAssignmentEntryPaths,
  shouldFilterRepositoryByMyAssignments,
} from './repository-assignment-access.util';
import { normalizeRepositoryTaskPath } from '../dashboard/dashboard-task-navigation.util';

const EXPLORER_ROOT_PATH = REPOSITORY_EXPLORER_ROOT_PATH;

type RepositoryViewMode = 'table' | 'explorer';

const EXPIRATION_OPTIONS = [
  { label: 'Sin expiración (permanente)', value: 'permanent' },
  { label: '24 horas', value: '24' },
  { label: '48 horas', value: '48' },
];

@Component({
  selector: 'app-repository-admin',
  templateUrl: './repository-admin.component.html',
  styleUrls: ['./repository-admin.component.scss'],
  standalone: true,
  imports: [
    PageHeaderComponent,
    FieldLabelComponent,
    ButtonComponent,
    InputComponent,
    SelectComponent,
    CheckboxComponent,
    BadgeComponent,
    TableComponent,
    EmptyComponent,
    IconComponent,
    FileTreeComponent,
    VaultFilePreviewComponent,
    VaultModuleShellComponent,
    VaultPageAlertComponent,
  ],
})
export class RepositoryAdminComponent implements OnInit, OnDestroy {
  private readonly repositoryService = inject(RepositoryService);
  private readonly fileAssignmentService = inject(FileAssignmentService);
  private readonly profileService = inject(ProfileService);
  private readonly vaultTenantScope = inject(VaultTenantScopeService);
  private readonly serverContext = inject(ServerContextService);
  private readonly repoStore = inject(repositoryAdminStore);
  private readonly auth = inject(AuthService);
  private readonly operativeRoleSync = inject(VaultOperativeRoleSyncService);
  private readonly vaultAppToast = inject(VaultAppToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly explorerListInFlight = new Set<string>();

  private static readonly MODULE_LABEL = 'Bóveda Digital';

  /** Re-evalúa permisos de asignación cuando cambia la sesión (JWT / user_data). */
  private readonly sessionRevision = signal(0);

  readonly canAssignFiles = computed(() => {
    this.sessionRevision();
    return userCanAssignRepositoryFilesFromSession();
  });

  readonly isSuperAdminUser = computed(() => {
    this.sessionRevision();
    return this.auth.isVaultSuperadmin() || userIsSuperadminFromSession();
  });

  private readonly onVaultSessionUpdated = (): void => {
    const hadFullBrowse = hasFullRepositoryBrowseAccess();
    this.auth.syncVaultTenantFromAccessToken();
    this.refreshCanAssignFiles();
    this.syncRepositoryTenantFromSession();
    const scopeChanged = this.bindRepositoryPersistScope();
    const hasFullBrowse = hasFullRepositoryBrowseAccess();
    if (scopeChanged || hadFullBrowse !== hasFullBrowse) {
      this.resetRepositoryNavigationState();
      this.hydrateRepositoryStateFromStore();
      this.scheduleScopedRepositoryBootstrap(true);
      this.refreshAssignUserListsIfOpen();
      return;
    }
    // syncFromApi/finalize dispara vault-session-updated; no rearmar el bootstrap (parpadeo).
    if (this.scopedBootstrapInFlight || Date.now() < this.scopedSessionBootstrapCooldownUntil) {
      this.refreshAssignUserListsIfOpen();
      return;
    }
    this.scheduleScopedRepositoryBootstrap(true);
    this.refreshAssignUserListsIfOpen();
  };

  private readonly onFileAssignmentsChanged = (): void => {
    this.scheduleScopedRepositoryBootstrap(true);
    if (this.assignmentsDialogOpen()) {
      this.loadAssignmentsDialogData();
    }
  };

  private readonly onDocumentVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible' || hasFullRepositoryBrowseAccess()) {
      return;
    }
    this.scheduleScopedRepositoryBootstrap(true);
  };

  private readonly onVaultAdminUsersChanged = (): void => {
    this.refreshAssignUserListsIfOpen();
  };

  /** Recarga listas de usuarios en diálogos de asignación abiertos. */
  private refreshAssignUserListsIfOpen(): void {
    if (this.assignDialogOpen()) {
      this.loadUsersForAssign();
    }
    if (this.assignmentsDialogOpen()) {
      this.loadAssignmentsDialogData();
    }
  }

  private assignUsersRequestSeq = 0;
  private loadListRequestSeq = 0;
  private zipListRecoverInFlight = false;
  private zipOaisEmptyFallbackKey = '';
  private assignmentScopeRefreshSeq = 0;
  private assignmentScopeUserIdRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private boundRepositoryPersistScope = '';
  /** Evita carreras constructor + NavigationEnd + ngOnInit que cancelan el listado (#0000143). */
  private scopedBootstrapInFlight = false;
  private pendingScopedBootstrapForce = false;
  private scopedEmptyAssignmentsRetries = 0;
  private scopedBootstrapDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Evita que finalize()/vault-session-updated dispare otro bootstrap en cascada. */
  private scopedSessionBootstrapCooldownUntil = 0;
  readonly loading = signal(false);
  readonly loadingAction = signal(false);
  readonly accessDenied = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorTitle = signal('');
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);

  readonly currentPath = signal('');
  readonly searchTerm = signal('');
  readonly fileTypeFilter = signal<string>('all');
  readonly searchInZips = signal<boolean>(false);
  readonly currentZipPath = signal<string | null>(null);
  readonly zipInternalPath = signal('');
  readonly rows = signal<RepositoryListItem[]>([]);
  readonly selectedFiles = signal<string[]>([]);
  readonly tablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];
  readonly tablePageIndex = signal(0);
  readonly tableSortColumn = signal<keyof RepositoryListItem | string | null>('name');
  readonly tableSortDirection = signal<'asc' | 'desc' | null>('asc');
  /** Tabla de listado o explorador (mismos datos que la API vía `rows`). */
  readonly viewMode = signal<RepositoryViewMode>('table');
  /** Tras `applySearch`, el explorador agrupa y muestra los archivos devueltos por la API. */
  readonly repositorySearchActive = signal(false);
  /**
   * Listados por ruta (expandir en explorador) sin `loadList` global: path → filas.
   * Se limpia al recargar lista o búsqueda.
   */
  private readonly explorerChildCache = signal<Record<string, RepositoryListItem[]>>({});
  /** Subcarpetas cuyo contenido (desde caché) se muestra expandido. */
  private readonly explorerExpandedPaths = signal<Set<string>>(new Set());
  /** Misma lista que la tabla, con árbol perezoso (expand = `list`/`listZip` por nodo). */
  readonly explorerFileTree = computed(() => this.buildExplorerFileTree());
  readonly pageHeaderActions = computed<PageHeaderAction[]>(() => [
    {
      id: 'view-table',
      label: 'Vista tabla',
      icon: 'List',
      variant: this.viewMode() === 'table' ? 'primary' : 'secondary',
    },
    {
      id: 'view-explorer',
      label: 'Vista explorador',
      icon: 'FolderOpen',
      variant: this.viewMode() === 'explorer' ? 'primary' : 'secondary',
    },
  ]);
  /** Tenant de `GET /api/auth/me` / my-tenants (sesión Vault). */
  private readonly authMeTenantId = signal('');
  /** Tenant inferido al cargar usuarios o al elegir destinatario. */
  private readonly assignScopeTenantId = signal('');
  private lastAssignUsersRaw: AssignableAdminUser[] = [];
  readonly fileTypeOptions = [
    { label: 'Todos', value: 'all' },
    { label: 'PDF', value: 'pdf' },
    { label: 'Imagen', value: 'image' },
    { label: 'Texto', value: 'text' },
    { label: 'ZIP', value: 'zip' },
  ];

  readonly previewOpen = signal(false);
  readonly previewTitle = signal('');
  readonly previewMimeType = signal('');
  readonly previewSrcUrl = signal('');
  readonly previewSrcData = signal<Uint8Array | null>(null);
  readonly previewText = signal('');
  readonly previewDownloadName = signal('');
  readonly previewUnsupportedMessage = signal('');
  readonly previewLoading = signal(false);
  readonly previewError = signal('');
  readonly selectedExplorerPath = signal<string | null>(null);
  /** Archivo resaltado en el panel; la carpeta activa sigue en `selectedExplorerPath`. */
  readonly selectedExplorerFilePath = signal<string | null>(null);
  /** Carpeta cuyo listado debe permanecer visible al abrir/cerrar vista previa. */
  private readonly explorerPanelFolderPath = signal<string | null>(null);

  readonly assignDialogOpen = signal(false);
  readonly assignSaving = signal(false);
  readonly assignTargetItems = signal<AssignTargetItem[]>([]);
  readonly assignTargetIsDirectory = computed(() =>
    resolveAssignIsDirectoryFlag(this.assignTargetItems()),
  );
  readonly assignDialogTitle = computed(() => {
    const items = this.assignTargetItems();
    if (items.length > 1) {
      return `Asignar ${items.length} carpetas/archivos`;
    }
    if (items.length === 1) {
      return items[0].isDirectory ? 'Asignar carpeta' : 'Asignar archivo';
    }
    return 'Asignar';
  });
  readonly assignShowsOaisHint = computed(() => this.assignTargetItems().length > 0);
  /** Entrada activa cuando el usuario tiene varias asignaciones DIP (persistida en sesión). */
  readonly activeAssignmentEntryPath = signal('');
  readonly assignmentEntryOptions = computed(() => {
    const entries = resolveUniqueAssignmentEntryPaths(this.myFileAssignments());
    return entries.map((entry) => ({
      label: formatRepositoryContextPath(entry),
      value: entry,
    }));
  });
  readonly showAssignmentEntrySelector = computed(
    () =>
      shouldFilterRepositoryByMyAssignments(this.myFileAssignments()) &&
      this.assignmentEntryOptions().length > 1,
  );
  readonly explorerZipPath = signal<string | null>(null);
  readonly explorerZipInternalPath = signal('');
  readonly explorerZipFiles = signal<FileNode[]>([]);
  readonly explorerZipLoading = signal(false);
  /** Asignaciones directas al usuario (user_id) para acotar listados en cliente. */
  private readonly myFileAssignments = signal<FileAssignmentRecord[]>([]);
  /** Evita abrir directamente el interior de una carpeta asignada tras deep-link. */
  private readonly pendingAssignmentEntryRedirect = signal(false);

  readonly explorerVirtualFolder = computed<VirtualFolderPreview | null>(() => {
    const zipPath = this.explorerZipPath();
    if (!zipPath) {
      return null;
    }
    const parts = zipPath.split('/').filter(Boolean);
    const zipName = parts[parts.length - 1] || zipPath;
    const inner = this.explorerZipInternalPath().trim();
    const innerLabel = inner ? inner.split('/').filter(Boolean).pop() : null;
    return {
      name: innerLabel ? `${zipName} / ${innerLabel}` : zipName,
      files: this.explorerZipFiles(),
      loading: this.explorerZipLoading(),
      showExitZip: true,
    };
  });

  /** Solo carpetas reales del repositorio bajo DIP (no archivos ni vista ZIP). */
  readonly explorerAssignableFolder = computed(() => {
    if (this.explorerZipPath()) {
      return null;
    }
    const path = this.selectedExplorerPath();
    if (!path || path === EXPLORER_ROOT_PATH) {
      return null;
    }
    const node = this.findExplorerNodeByPath(this.explorerFileTree(), path);
    if (!node || node.type !== 'directory') {
      return null;
    }
    if (!isDipAssignablePath(node.path)) {
      return null;
    }
    return node;
  });
  readonly assignUserIds = signal<string[]>([]);
  readonly assignExpiration = signal<string>('permanent');
  readonly assignUserSearch = signal('');
  readonly assignUserPicker = signal('');
  readonly assignUsersLoading = signal(false);
  readonly assignUsersLoadHint = signal<string | null>(null);
  readonly availableUsersForAssign = signal<AssignableAdminUser[]>([]);
  readonly assignExpirationOptions = EXPIRATION_OPTIONS;
  readonly repositoryListEmptyDescription = computed(() => {
    if (
      shouldFilterRepositoryByMyAssignments(this.myFileAssignments()) &&
      this.myFileAssignments().length === 0
    ) {
      return 'No tiene carpetas ni archivos asignados en Bóveda Digital. Contacte al administrador.';
    }
    return 'No hay archivos para la ruta/consulta actual.';
  });

  readonly filteredAssignUsers = computed(() => {
    const q = this.assignUserSearch().trim().toLowerCase();
    const users = this.availableUsersForAssign();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  });

  readonly assignUserSelectOptions = computed(() => {
    const picked = new Set(this.assignUserIds());
    return this.filteredAssignUsers()
      .filter((u) => !picked.has(u.id))
      .map((u) => ({
        label: buildAssignUserCheckboxLabel(u),
        value: u.id,
      }));
  });

  readonly selectedAssignUsers = computed(() => {
    const picked = new Set(this.assignUserIds());
    return this.availableUsersForAssign().filter((u) => picked.has(u.id));
  });

  readonly assignmentsDialogOpen = signal(false);
  readonly assignmentsLoading = signal(false);
  readonly assignmentsUnassigning = signal(false);
  readonly allAssignments = signal<FileAssignmentRecord[]>([]);
  readonly selectedAssignmentIds = signal<Set<string>>(new Set());
  readonly assignmentUsersById = signal<Map<string, AssignableAdminUser>>(new Map());
  readonly assignmentGroups = computed(() =>
    groupFileAssignments(this.allAssignments(), this.assignmentUsersById()),
  );
  readonly selectedAssignmentCount = computed(() => this.selectedAssignmentIds().size);

  readonly tableConfig = computed<TableConfig<RepositoryListItem>>(() => {
    const baseActions: TableConfig<RepositoryListItem>['actions'] = [];

    if (this.canAssignFiles()) {
      baseActions.push({
        label: 'Asignar documento',
        icon: 'UserCheck',
        action: 'assign',
        visible: (row) => this.canAssignRepositoryItem(this.getPath(row)),
      });
    }

      baseActions.push(
      {
        label: 'Abrir',
        icon: 'view',
        action: 'open',
        visible: (row) => {
          if (this.isFolder(row)) {
            return true;
          }
          // AIP: no abrir/consultar .zip del archivo interno (#0000143 C5).
          if (this.isZipRow(row) && this.isRowUnderAip(row)) {
            return false;
          }
          return this.isZipRow(row);
        },
      },
      {
        label: 'Vista previa',
        icon: 'Eye',
        action: 'preview',
        visible: (row) => !this.isFolder(row) && !this.isZipRow(row),
      },
      {
        label: 'Descargar',
        icon: 'Download',
        action: 'download',
        visible: () => true,
      },
    );

    return {
      columns: [
        { key: 'name', label: 'Nombre', sortable: true, cellTemplate: true },
        { key: 'type', label: 'Tipo', sortable: true, align: 'center', width: '110px', cellTemplate: true },
        { key: 'size', label: 'Tamaño', sortable: true, align: 'right', width: '120px', cellTemplate: true },
        { key: 'modified_at', label: 'Modificado', sortable: true, align: 'center', width: '180px' },
      ],
      actions: baseActions,
      showPagination: true,
      rowClickable: true,
      pageSize: readVaultItemsPerPageFromPreferences(),
      pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
      initialSort: { column: 'name', direction: 'asc' },
    };
  });

  ngOnInit(): void {
    this.refreshCanAssignFiles();
  }

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('vault-session-updated', this.onVaultSessionUpdated);
      window.addEventListener(VAULT_ADMIN_USERS_CHANGED_EVENT, this.onVaultAdminUsersChanged);
      window.addEventListener(VAULT_FILE_ASSIGNMENTS_CHANGED_EVENT, this.onFileAssignmentsChanged);
      document.addEventListener('visibilitychange', this.onDocumentVisibilityChange);
    }
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event.urlAfterRedirects.includes('/repository-admin')) {
          this.scheduleScopedRepositoryBootstrap(true);
        }
      });
    this.viewMode.set('table');
    this.repoStore.setViewMode('table');
    this.auth.syncVaultTenantFromAccessToken();
    this.syncRepositoryTenantFromSession();
    this.bindRepositoryPersistScope();
    this.hydrateRepositoryStateFromStore();
    this.hydrateAssignTenantScope();
    this.refreshCanAssignFiles();
    this.applyRepositoryDeepLinkFromRoute(this.route.snapshot.queryParamMap.get('target'));
    if (hasFullRepositoryBrowseAccess()) {
      this.loadList();
    } else {
      // Esperar tenant + /auth/me (vault_user_id) antes del primer listado.
      this.operativeRoleSync
        .syncFromApi({ force: true })
        .pipe(
          switchMap(() => this.vaultTenantScope.ensureHydrated$()),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.bindRepositoryPersistScope();
          this.fileAssignmentService.invalidateAssignedCache();
          this.scheduleScopedRepositoryBootstrap(true);
        });
    }
  }

  ngOnDestroy(): void {
    if (this.explorerTreeSearchTimer) {
      clearTimeout(this.explorerTreeSearchTimer);
      this.explorerTreeSearchTimer = null;
    }
    if (this.assignmentScopeUserIdRetryTimer) {
      clearTimeout(this.assignmentScopeUserIdRetryTimer);
      this.assignmentScopeUserIdRetryTimer = null;
    }
    if (this.scopedBootstrapDebounceTimer) {
      clearTimeout(this.scopedBootstrapDebounceTimer);
      this.scopedBootstrapDebounceTimer = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('vault-session-updated', this.onVaultSessionUpdated);
      window.removeEventListener(VAULT_ADMIN_USERS_CHANGED_EVENT, this.onVaultAdminUsersChanged);
      window.removeEventListener(VAULT_FILE_ASSIGNMENTS_CHANGED_EVENT, this.onFileAssignmentsChanged);
      document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange);
    }
  }

  private refreshCanAssignFiles(): void {
    this.auth.ensureCurrentUserFromToken();
    syncRoleIdsIntoUserData();
    this.sessionRevision.update((n) => n + 1);
    this.cdr.markForCheck();
  }

  /** Enlaza sessionStorage de bóveda al tenant + usuario en sesión (#0000143). */
  private bindRepositoryPersistScope(): boolean {
    this.auth.ensureCurrentUserFromToken();
    const userId = vaultCurrentUserIdFromSession();
    const tenantId = vaultCurrentTenantIdFromSession() || this.authMeTenantId();
    const scopeKey = `${tenantId}|${userId}`;
    const changed = scopeKey !== this.boundRepositoryPersistScope;
    this.boundRepositoryPersistScope = scopeKey;
    const storeScopeChanged = this.repoStore.bindPersistScope(tenantId, userId);
    if (changed || storeScopeChanged) {
      this.hydrateRepositoryStateFromStore();
    }
    return changed || storeScopeChanged;
  }

  private hydrateRepositoryStateFromStore(): void {
    this.currentPath.set(this.repoStore.currentPath());
    this.searchTerm.set(this.repoStore.searchTerm());
    this.fileTypeFilter.set(this.repoStore.fileTypeFilter());
    this.searchInZips.set(false);
    this.repoStore.setSearchInZips(false);
    this.currentZipPath.set(this.repoStore.currentZipPath());
    let restoredZipInternal =
      sanitizeZipInternalPathForList(this.repoStore.zipInternalPath()) ?? '';
    // No rehidratar ya dentro de data/ en paquetes OAIS (oculta Metadata/manifiesto).
    const restoredZip = this.repoStore.currentZipPath();
    if (
      restoredZip &&
      /^data(\/|$)/i.test(restoredZipInternal) &&
      (isUnderDipSegment(restoredZip) ||
        isAipFolderPath(restoredZip) ||
        isSipFolderPath(restoredZip))
    ) {
      restoredZipInternal = '';
      this.repoStore.setZipInternalPath('');
    }
    this.zipInternalPath.set(restoredZipInternal);
    const prefPageSize = readVaultItemsPerPageFromPreferences();
    const storedPageSize = this.repoStore.tablePageSize();
    this.tablePageSize.set(prefPageSize);
    if (prefPageSize !== storedPageSize) {
      this.tablePageIndex.set(0);
      this.repoStore.setTableState({ pageSize: prefPageSize, pageIndex: 0 });
    } else {
      this.tablePageIndex.set(this.repoStore.tablePageIndex());
    }
    bindVaultTablePageSizeToPreferences(this.tablePageSize, this.destroyRef, (n) => {
      this.tablePageIndex.set(0);
      this.repoStore.setTableState({ pageSize: n, pageIndex: 0 });
    });
    this.tableSortColumn.set(
      this.repoStore.tableSortColumn() as keyof RepositoryListItem | string | null,
    );
    this.tableSortDirection.set(this.repoStore.tableSortDirection());
    const normalizedExpanded = Array.from(
      new Set(
        this.repoStore
          .explorerExpandedPaths()
          .map((path) => normalizeRepositoryExplorerPath(path))
          .filter((path) => path && path !== EXPLORER_ROOT_PATH),
      ),
    );
    this.explorerExpandedPaths.set(new Set(normalizedExpanded));
    if (normalizedExpanded.length !== this.repoStore.explorerExpandedPaths().length) {
      this.repoStore.setExplorerExpandedPaths(normalizedExpanded);
    }
    this.selectedExplorerPath.set(this.repoStore.selectedExplorerPath());
    this.activeAssignmentEntryPath.set(this.repoStore.activeAssignmentEntryPath());
  }

  private resetRepositoryNavigationState(): void {
    this.repositorySearchActive.set(false);
    this.myFileAssignments.set([]);
    this.pendingAssignmentEntryRedirect.set(false);
    this.explorerChildCache.set({});
    this.repoStore.resetNavigationState();
    this.currentPath.set('');
    this.currentZipPath.set(null);
    this.zipInternalPath.set('');
    this.selectedExplorerPath.set(null);
    this.activeAssignmentEntryPath.set('');
    this.searchTerm.set('');
    this.fileTypeFilter.set('all');
    this.searchInZips.set(false);
    this.rows.set([]);
    this.selectedFiles.set([]);
    this.info.set(null);
    this.zipOaisEmptyFallbackKey = '';
  }

  isSuperAdmin(): boolean {
    return this.auth.isVaultSuperadmin() || userIsSuperadminFromSession();
  }

  canAssignRepositoryItem(path: string): boolean {
    if (!this.canAssignFiles()) {
      return false;
    }
    return isDipAssignablePath(path);
  }

  readonly canAssignRepositoryItemFn = (path: string): boolean =>
    this.canAssignRepositoryItem(path);

  onActiveAssignmentEntryChange(entryPath: string): void {
    const next = (entryPath ?? '').trim();
    if (!next) {
      return;
    }
    this.setActiveAssignmentEntryPath(next);
    this.repositorySearchActive.set(false);
    this.closeExplorerZip();
    this.currentZipPath.set(null);
    this.repoStore.setCurrentZipPath(null);
    this.zipInternalPath.set('');
    this.repoStore.setZipInternalPath('');
    this.currentPath.set(next);
    this.repoStore.setCurrentPath(next);
    const explorerPath = next.trim() || EXPLORER_ROOT_PATH;
    this.selectedExplorerPath.set(explorerPath);
    this.repoStore.setSelectedExplorerPath(explorerPath);
    this.loadList();
  }

  private setActiveAssignmentEntryPath(path: string): void {
    const normalized = (path ?? '').trim();
    this.activeAssignmentEntryPath.set(normalized);
    this.repoStore.setActiveAssignmentEntryPath(normalized);
  }

  private syncActiveAssignmentEntryPath(assignments: FileAssignmentRecord[]): void {
    if (!shouldFilterRepositoryByMyAssignments(assignments)) {
      this.setActiveAssignmentEntryPath('');
      return;
    }
    const entries = resolveUniqueAssignmentEntryPaths(assignments);
    if (!entries.length) {
      this.setActiveAssignmentEntryPath('');
      return;
    }
    const current = this.activeAssignmentEntryPath();
    if (current && entries.some((entry) => entry === current)) {
      return;
    }
    this.setActiveAssignmentEntryPath(entries[0]!);
  }

  private preferredAssignmentEntry(): string {
    return this.activeAssignmentEntryPath();
  }

  isAdminEmpresa(): boolean {
    return this.auth.isVaultAdminEmpresa() || userIsAdminEmpresaFromSession();
  }

  setViewMode(mode: RepositoryViewMode): void {
    this.viewMode.set(mode);
    this.repoStore.setViewMode(mode);
    if (mode === 'explorer' && !this.repositorySearchActive()) {
      this.searchTerm.set('');
      this.repoStore.setSearchTerm('');
      this.syncExplorerFolderPreviewSelection();
    }
  }

  /** Selecciona la carpeta activa del explorador para mostrar su contenido en el panel derecho. */
  private syncExplorerFolderPreviewSelection(): void {
    if (this.repositorySearchActive() || this.explorerZipPath()) {
      return;
    }
    if (this.rows().length === 0 && !this.currentPath()) {
      return;
    }
    const path = this.currentPath().trim() || EXPLORER_ROOT_PATH;
    this.selectedExplorerPath.set(path);
    this.explorerPanelFolderPath.set(path);
    this.selectedExplorerFilePath.set(null);
    this.repoStore.setSelectedExplorerPath(path);
    this.ensureExplorerPathExpanded(this.currentPath());
  }

  /** Expande en el árbol los ancestros de la ruta actual (p. ej. CONSULTAS/DIP). */
  private ensureExplorerPathExpanded(path: string): void {
    const prefixes = buildRepositoryPathPrefixes(path);
    if (prefixes.length === 0) {
      return;
    }
    this.updateExplorerExpandedPaths((current) => {
      const next = new Set(current);
      for (const prefix of prefixes) {
        next.add(prefix);
      }
      return next;
    });
  }

  /** Persiste rutas expandidas normalizadas (`/home/...`) en sessionStorage. */
  private updateExplorerExpandedPaths(updater: (current: Set<string>) => Set<string>): void {
    this.explorerExpandedPaths.update((current) => {
      const next = updater(new Set(current));
      const normalized = new Set(
        Array.from(next)
          .map((p) => normalizeRepositoryExplorerPath(p))
          .filter((p) => p && p !== EXPLORER_ROOT_PATH),
      );
      this.repoStore.setExplorerExpandedPaths(Array.from(normalized));
      return normalized;
    });
  }

  onPageHeaderAction(actionId: string): void {
    if (actionId === 'view-table') {
      this.setViewMode('table');
    }
    if (actionId === 'view-explorer') {
      this.setViewMode('explorer');
    }
    if (actionId === 'assignments') {
      this.openAssignmentsDialog();
    }
  }

  /** Misma resolución que `RepositoryService` / `FileAssignmentService` (tenant desde sesión Vault). */
  private syncRepositoryTenantFromSession(): void {
    const tenantId = syncVaultTenantScopeFromSession(this.serverContext);
    if (tenantId) {
      this.applyResolvedAssignTenantId(tenantId);
    }
  }

  /** Misma hidratación que Ingest / resto de Vault (`VaultTenantScopeService`). */
  private hydrateAssignTenantScope(): void {
    this.vaultTenantScope.ensureHydrated$().subscribe((tid) => {
      if (tid) {
        this.applyResolvedAssignTenantId(tid);
      }
    });
  }

  private applyResolvedAssignTenantId(tenantId: string): void {
    const id = (tenantId || '').trim();
    if (!id) {
      return;
    }
    const previous = this.authMeTenantId();
    this.authMeTenantId.set(id);
    persistActiveTenantId(id, this.serverContext);
    if (previous && previous !== id) {
      const scopeChanged = this.bindRepositoryPersistScope();
      if (scopeChanged) {
        this.hydrateRepositoryStateFromStore();
      }
    }
  }

  /** Paridad `RepositoryService` / Ingest + señales del diálogo Asignar. */
  private resolveActiveAssignTenantId(): string {
    this.auth.syncVaultTenantFromAccessToken();
    syncVaultTenantScopeFromSession(this.serverContext);

    const usersPool = [...this.lastAssignUsersRaw, ...this.availableUsersForAssign()];

    return firstNonEmptyTenantId(
      this.assignScopeTenantId().trim(),
      tenantIdFromAssignUsers(usersPool, this.assignUserIds()),
      this.authMeTenantId().trim(),
      this.vaultTenantScope.apiTenantId(),
    );
  }

  private syncTenantFromSelectedAssignUsers(): void {
    const tid =
      tenantIdFromAssignUsers(this.availableUsersForAssign(), this.assignUserIds()) ||
      tenantIdFromAssignUsers(this.lastAssignUsersRaw, this.assignUserIds());
    if (tid) {
      this.assignScopeTenantId.set(tid);
      this.applyResolvedAssignTenantId(tid);
    }
  }

  openAssignmentsDialog(): void {
    if (!this.canAssignFiles()) {
      return;
    }
    this.assignmentsDialogOpen.set(true);
    this.selectedAssignmentIds.set(new Set());
    this.loadAssignmentsDialogData();
  }

  closeAssignmentsDialog(): void {
    this.assignmentsDialogOpen.set(false);
    this.selectedAssignmentIds.set(new Set());
  }

  isAssignmentGroupSelected(group: RepositoryAssignmentGroup): boolean {
    const selected = this.selectedAssignmentIds();
    return (
      group.assignmentIds.length > 0 &&
      group.assignmentIds.every((id) => selected.has(id))
    );
  }

  toggleAssignmentGroup(group: RepositoryAssignmentGroup, checked: boolean): void {
    this.selectedAssignmentIds.update((prev) => {
      const next = new Set(prev);
      for (const id of group.assignmentIds) {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  }

  selectAllAssignments(): void {
    const ids = this.allAssignments().map((a) => a.id).filter(Boolean);
    this.selectedAssignmentIds.set(new Set(ids));
  }

  clearAssignmentSelection(): void {
    this.selectedAssignmentIds.set(new Set());
  }

  unassignSelectedAssignments(): void {
    const ids = Array.from(this.selectedAssignmentIds());
    if (ids.length === 0) {
      return;
    }
    openVaultConfirmDialog(this.dialogService, {
      title: 'Desasignar elementos',
      message: `¿Desasignar ${ids.length} elemento(s)? Esta acción no se puede deshacer.`,
      confirmLabel: 'Desasignar',
      variant: 'warning',
    }).subscribe((confirmed) => {
      if (confirmed) {
        this.runBulkUnassign(ids);
      }
    });
  }

  unassignAllAssignments(): void {
    const ids = this.allAssignments().map((a) => a.id).filter(Boolean);
    if (ids.length === 0) {
      return;
    }
    openVaultConfirmDialog(this.dialogService, {
      title: 'Desasignar todas las asignaciones',
      message: `¿Desasignar todas las asignaciones (${ids.length})? Esta acción no se puede deshacer.`,
      confirmLabel: 'Desasignar',
      variant: 'warning',
    }).subscribe((confirmed) => {
      if (confirmed) {
        this.runBulkUnassign(ids);
      }
    });
  }

  unassignAssignmentGroup(group: RepositoryAssignmentGroup): void {
    if (group.assignmentIds.length === 0) {
      return;
    }
    const user = group.users[0];
    const userLabel = user
      ? this.formatAssignUserLabel(user)
      : 'este usuario';
    openVaultConfirmDialog(this.dialogService, {
      title: 'Desasignar usuario',
      message: `¿Desasignar a ${userLabel} de "${group.fileName}"? Esta acción no se puede deshacer.`,
      confirmLabel: 'Desasignar',
      variant: 'warning',
    }).subscribe((confirmed) => {
      if (confirmed) {
        this.runBulkUnassign(group.assignmentIds);
      }
    });
  }

  formatAssignmentRoleLabel = formatAssignRoleLabel;

  assignmentExpiryLabel(group: RepositoryAssignmentGroup): string {
    if (!group.expiresAt) {
      return 'Permanente';
    }
    if (group.isExpired) {
      return 'Expirada';
    }
    const diff = new Date(group.expiresAt).getTime() - Date.now();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `Expira en ${hours}h ${minutes}m`;
    }
    return `Expira en ${minutes}m`;
  }

  private loadAssignmentsDialogData(): void {
    this.assignmentsLoading.set(true);
    this.fileAssignmentService
      .getAssigned(undefined, undefined, { cacheNamespace: 'admin-all', force: true })
      .pipe(finalize(() => this.assignmentsLoading.set(false)))
      .subscribe({
        next: (assignments) => {
          const seen = new Set<string>();
          const unique = assignments.filter((a) => {
            if (!a.id || seen.has(a.id)) {
              return false;
            }
            seen.add(a.id);
            return true;
          });
          this.allAssignments.set(unique);
          this.loadAssignmentUsersMap(unique);
        },
        error: () => {
          this.allAssignments.set([]);
          this.vaultAppToast.show({
            type: 'error',
            title: 'Asignaciones',
            message: 'No se pudieron cargar las asignaciones.',
          });
        },
      });
  }

  private loadAssignmentUsersMap(assignments: FileAssignmentRecord[]): void {
    const userIds = new Set<string>();
    for (const row of assignments) {
      if (row.user_id) {
        userIds.add(row.user_id);
      }
    }
    if (userIds.size === 0) {
      this.assignmentUsersById.set(new Map());
      return;
    }
    this.fileAssignmentService.listUsersForAssign(this.isSuperAdmin()).subscribe({
      next: (users) => {
        const map = new Map<string, AssignableAdminUser>();
        for (const user of users) {
          if (userIds.has(user.id)) {
            map.set(user.id, user);
          }
        }
        this.assignmentUsersById.set(map);
      },
      error: () => {
        this.assignmentUsersById.set(new Map());
      },
    });
  }

  private runBulkUnassign(ids: string[]): void {
    if (ids.length === 0) {
      return;
    }
    this.assignmentsUnassigning.set(true);
    let completed = 0;
    let failed = 0;
    for (const id of ids) {
      this.fileAssignmentService.unassign(id).subscribe({
        next: () => {
          completed += 1;
          this.afterUnassignBatch(completed, failed, ids.length);
        },
        error: () => {
          failed += 1;
          this.afterUnassignBatch(completed, failed, ids.length);
        },
      });
    }
  }

  private afterUnassignBatch(completed: number, failed: number, total: number): void {
    if (completed + failed < total) {
      return;
    }
    this.assignmentsUnassigning.set(false);
    if (failed === 0) {
      this.vaultAppToast.show({
        type: 'success',
        title: 'Desasignación',
        message: `Se desasignaron ${completed} elemento(s).`,
      });
      notifyVaultFileAssignmentsChanged();
    } else if (completed > 0) {
      this.vaultAppToast.show({
        type: 'warning',
        title: 'Desasignación parcial',
        message: `${completed} correcto(s), ${failed} con error.`,
      });
    } else {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Desasignación',
        message: 'No se pudo desasignar.',
      });
    }
    this.selectedAssignmentIds.set(new Set());
    this.loadAssignmentsDialogData();
    this.refreshRepositoryAssignmentScope(true, true);
  }

  onExplorerTreeExpandAction(event: { path: string; action: 'load' | 'toggle' }): void {
    if (event.path === EXPLORER_ROOT_PATH) {
      return;
    }
    const folderPath = normalizeRepositoryExplorerPath(event.path);
    if (!folderPath || folderPath === EXPLORER_ROOT_PATH) {
      return;
    }
    if (event.action === 'toggle') {
      this.updateExplorerExpandedPaths((current) => {
        const next = new Set(current);
        if (next.has(folderPath)) {
          next.delete(folderPath);
        } else {
          next.add(folderPath);
        }
        return next;
      });
      return;
    }
    this.updateExplorerExpandedPaths((current) => new Set(current).add(folderPath));
    this.loadExplorerFolderChildren(folderPath);
  }

  private explorerTreeSearchTimer: ReturnType<typeof setTimeout> | null = null;

  /** Criterios que disparan búsqueda API (nombre ≥2 chars o tipo distinto de Todos). */
  private hasRepositorySearchCriteria(): boolean {
    const term = this.searchTerm().trim();
    return term.length >= 2 || this.fileTypeFilter() !== 'all';
  }

  /** Filtros activos en UI (incluye ZIP y texto corto). */
  hasRepositoryFilterUiState(): boolean {
    return (
      this.hasRepositorySearchCriteria() ||
      this.searchInZips() ||
      this.searchTerm().trim().length > 0
    );
  }

  /** Vuelve a jerarquía inicial si no quedan criterios de búsqueda. */
  private resetRepositoryViewIfNoCriteria(wasTypeFiltered = false): void {
    if (this.hasRepositorySearchCriteria() || this.searchInZips()) {
      return;
    }
    if (this.repositorySearchActive()) {
      this.restoreRoot();
      return;
    }
    const atRoot = !this.currentPath() && !this.currentZipPath();
    if (wasTypeFiltered && atRoot && this.rows().length === 0) {
      this.restoreRoot();
    }
  }

  onSearchTermChange(value: string): void {
    this.searchTerm.set(value ?? '');
    this.repoStore.setSearchTerm(this.searchTerm());
    this.resetRepositoryViewIfNoCriteria();
  }

  /** Buscador del árbol: escribe y dispara búsqueda API (debounce). */
  onExplorerTreeSearchChange(term: string): void {
    this.onSearchTermChange(term);
    if (this.explorerTreeSearchTimer) {
      clearTimeout(this.explorerTreeSearchTimer);
      this.explorerTreeSearchTimer = null;
    }
    const trimmed = term.trim();
    if (trimmed.length >= 2) {
      this.explorerTreeSearchTimer = setTimeout(() => {
        this.explorerTreeSearchTimer = null;
        this.runExplorerGlobalSearch();
      }, 600);
      return;
    }
    this.resetRepositoryViewIfNoCriteria();
  }

  /** Enter en el buscador del árbol → búsqueda inmediata. */
  onExplorerTreeSearchSubmit(term: string): void {
    this.onSearchTermChange(term);
    if (this.explorerTreeSearchTimer) {
      clearTimeout(this.explorerTreeSearchTimer);
      this.explorerTreeSearchTimer = null;
    }
    const trimmed = term.trim();
    if (trimmed.length >= 2) {
      this.runExplorerGlobalSearch();
      return;
    }
    this.resetRepositoryViewIfNoCriteria();
  }

  private runExplorerGlobalSearch(): void {
    if (this.viewMode() !== 'explorer') {
      this.setViewMode('explorer');
    }
    this.applySearch();
  }

  onFileTypeFilterChange(value: string): void {
    const wasTypeFiltered = this.fileTypeFilter() !== 'all';
    this.fileTypeFilter.set(value || 'all');
    this.repoStore.setFileTypeFilter(this.fileTypeFilter());
    if (!this.hasRepositorySearchCriteria()) {
      this.resetRepositoryViewIfNoCriteria(wasTypeFiltered);
      return;
    }
    if (this.repositorySearchActive()) {
      this.applySearch();
    }
  }

  onSearchInZipsChange(value: boolean): void {
    this.searchInZips.set(!!value);
    this.repoStore.setSearchInZips(this.searchInZips());
    if (!this.searchInZips()) {
      this.resetRepositoryViewIfNoCriteria();
      return;
    }
    if (this.repositorySearchActive() && this.hasRepositorySearchCriteria()) {
      this.applySearch();
    }
  }

  onExplorerFolderSelected(node: FileNode): void {
    this.closeExplorerZip();
    this.selectedExplorerPath.set(node.path);
    this.explorerPanelFolderPath.set(node.path);
    this.selectedExplorerFilePath.set(null);
    this.repoStore.setSelectedExplorerPath(node.path);
  }

  onExplorerFileSelected(node: FileNode): void {
    const path = (node.path ?? '').trim();
    if (!path) {
      return;
    }
    const row = this.fileNodeToRepositoryItem(node);
    if (this.explorerZipPath()) {
      this.handleExplorerZipPanelSelection(node, row);
      return;
    }
    if (this.isZipRow(row)) {
      this.openExplorerZip(node);
      return;
    }
    const folderPath = this.resolveExplorerPanelFolderPath(path);
    if (folderPath) {
      this.explorerPanelFolderPath.set(folderPath);
      this.selectedExplorerPath.set(folderPath);
      this.repoStore.setSelectedExplorerPath(folderPath);
    }
    this.selectedExplorerFilePath.set(path);
    this.previewRow(row);
  }

  onExplorerExitVirtualFolder(): void {
    this.closeExplorerZip();
  }

  onExplorerFileDownloaded(node: FileNode): void {
    const row = this.fileNodeToRepositoryItem(node);
    this.downloadOne(row, this.explorerZipPath());
  }

  private openExplorerZip(node: FileNode): void {
    const zipPath = this.resolveExplorerZipArchivePath(node);
    if (!zipPath) {
      return;
    }
    if (isAipFolderPath(zipPath) || isAipFolderPath(this.selectedExplorerPath() ?? '')) {
      this.vaultAppToast.show({
        type: 'info',
        title: 'AIP',
        message:
          'Según OAIS, el contenido AIP es de archivo interno y no se abre para consulta. Use la carpeta DIP del proyecto.',
      });
      return;
    }
    this.explorerZipPath.set(zipPath);
    this.explorerZipInternalPath.set('');
    this.loadExplorerZipContents();
  }

  private closeExplorerZip(): void {
    this.explorerZipPath.set(null);
    this.explorerZipInternalPath.set('');
    this.explorerZipFiles.set([]);
    this.explorerZipLoading.set(false);
    this.restoreExplorerPathAfterZip();
  }

  /** Tras salir de un ZIP, la selección no debe quedar en la ruta del archivo .zip. */
  private restoreExplorerPathAfterZip(): void {
    const selected = (this.selectedExplorerPath() ?? '').trim();
    if (!selected || selected === EXPLORER_ROOT_PATH || !this.looksLikeRepositoryFilePath(selected)) {
      return;
    }
    const parts = selected.split('/').filter(Boolean);
    parts.pop();
    const parent = parts.join('/');
    this.selectedExplorerPath.set(parent || null);
    this.repoStore.setSelectedExplorerPath(parent || null);
  }

  private loadExplorerZipContents(): void {
    const zipPath = this.explorerZipPath();
    if (!zipPath) {
      return;
    }
    this.explorerZipLoading.set(true);
    this.repositoryService
      .listZip(zipPath, this.explorerZipInternalPath() || undefined)
      .pipe(
        timeout(45000),
        finalize(() => this.explorerZipLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res: unknown) => {
          this.resolveOaisZipRootListing$(
            zipPath,
            this.explorerZipInternalPath(),
            res,
          )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((items) => {
              this.explorerZipFiles.set(items.map((row) => this.repositoryRowToFileNode(row, true)));
            });
        },
        error: (err: unknown) => {
          this.explorerZipFiles.set([]);
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: RepositoryAdminComponent.MODULE_LABEL,
              fallback: 'No se pudo abrir el contenido del ZIP.',
            }),
          );
        },
      });
  }

  private handleExplorerZipPanelSelection(node: FileNode, row: RepositoryListItem): void {
    if (this.isZipNavigationEntry(node.name ?? '')) {
      return;
    }
    if (node.type === 'directory') {
      const next = resolveZipInternalNavigatePath(
        this.explorerZipInternalPath(),
        resolveZipInternalRowSegment(row),
      );
      this.explorerZipInternalPath.set(next);
      this.loadExplorerZipContents();
      return;
    }
    if (this.isZipRow(row)) {
      this.openExplorerZip(node);
      return;
    }
    this.previewRow(
      { ...row, internal_path: row.path, path: row.path },
      this.explorerZipPath(),
    );
  }

  private fileNodeToRepositoryItem(node: FileNode): RepositoryListItem {
    return {
      name: node.name,
      path: node.path,
      type: node.type === 'directory' ? 'folder' : 'file',
      extension: node.extension ?? null,
      size: node.size ?? null,
      internal_path: node.type !== 'directory' ? node.path : null,
    };
  }

  private repositoryRowToFileNode(row: RepositoryListItem, insideZip = false): FileNode {
    const path = insideZip
      ? row.internal_path || row.path || row.name
      : this.getPath(row) || row.name;
    if (this.isFolder(row)) {
      return {
        name: row.name,
        path,
        type: 'directory',
      };
    }
    return {
      name: row.name,
      path,
      type: 'file',
      size: row.size ?? undefined,
      extension: row.extension ?? undefined,
    };
  }

  private applyRepositoryDeepLinkFromRoute(raw: string | null | undefined): void {
    const target = raw?.trim();
    if (!target) {
      return;
    }
    const deepLinkPath = resolveRepositoryDeepLinkListPath(target);
    if (!deepLinkPath) {
      return;
    }

    this.repositorySearchActive.set(false);
    this.closeExplorerZip();
    this.currentZipPath.set(null);
    this.repoStore.setCurrentZipPath(null);
    this.zipInternalPath.set('');
    this.repoStore.setZipInternalPath('');

    const zipVirtual = splitZipVirtualAssignmentPath(deepLinkPath);
    if (zipVirtual) {
      const zipParent = getParentRepositoryExplorerPath(zipVirtual.zipPath);
      this.setActiveAssignmentEntryPath(zipParent);
      this.currentPath.set(zipParent);
      this.repoStore.setCurrentPath(zipParent);
      this.currentZipPath.set(zipVirtual.zipPath);
      this.repoStore.setCurrentZipPath(zipVirtual.zipPath);
      const innerListPath = getParentZipInternalPath(zipVirtual.internalPath);
      this.zipInternalPath.set(innerListPath);
      this.repoStore.setZipInternalPath(innerListPath);
      const explorerPath = zipParent.trim() || EXPLORER_ROOT_PATH;
      this.selectedExplorerPath.set(explorerPath);
      this.repoStore.setSelectedExplorerPath(explorerPath);
      return;
    }

    this.currentPath.set(deepLinkPath);
    this.repoStore.setCurrentPath(deepLinkPath);
    this.setActiveAssignmentEntryPath(deepLinkPath);
    this.pendingAssignmentEntryRedirect.set(true);

    const explorerPath = this.currentPath().trim() || EXPLORER_ROOT_PATH;
    this.selectedExplorerPath.set(explorerPath);
    this.repoStore.setSelectedExplorerPath(explorerPath);
    this.ensureExplorerPathExpanded(this.currentPath());
  }

  private resolveAssignmentScopePathCorrection(): string | null {
    const mine = this.myFileAssignments();
    if (!shouldFilterRepositoryByMyAssignments(mine)) {
      return null;
    }

    const zipPath = this.currentZipPath();
    if (zipPath) {
      if (isRepositoryZipContextAllowed(zipPath, this.zipInternalPath(), mine)) {
        return null;
      }
      return resolveAssignmentScopedHomePath(mine, this.preferredAssignmentEntry());
    }

    const listingCorrection = resolveAssignmentListingPathCorrection(this.currentPath(), mine);
    if (listingCorrection) {
      return listingCorrection;
    }

    const deepLinkTarget = this.route.snapshot.queryParamMap.get('target');
    if (deepLinkTarget) {
      const listPath = resolveRepositoryDeepLinkListPath(deepLinkTarget);
      if (listPath && isRepositoryNavigationPathAllowed(listPath, mine)) {
        return null;
      }
    }

    const current = normalizeRepositoryExplorerPath(this.currentPath());
    if (!current) {
      return resolveAssignmentScopedHomePath(mine, this.preferredAssignmentEntry());
    }
    if (!isRepositoryNavigationPathAllowed(current, mine)) {
      return resolveAssignmentScopedHomePath(mine, this.preferredAssignmentEntry());
    }
    return null;
  }

  private applyAssignmentScopedPath(path: string, preserveZip = false): void {
    this.repositorySearchActive.set(false);
    if (!preserveZip) {
      this.closeExplorerZip();
      this.currentZipPath.set(null);
      this.repoStore.setCurrentZipPath(null);
      this.zipInternalPath.set('');
      this.repoStore.setZipInternalPath('');
    }
    this.currentPath.set(path);
    this.repoStore.setCurrentPath(path);
    const explorerPath = path.trim() || EXPLORER_ROOT_PATH;
    this.selectedExplorerPath.set(explorerPath);
    this.repoStore.setSelectedExplorerPath(explorerPath);
  }

  /**
   * Ruta de listado antes de llamar al API: inicio acotado, deep-link y carpeta padre
   * cuando el target es la raíz de una asignación.
   */
  private resolveEffectiveListPathBeforeFetch(
    listPath: string | undefined,
    assignments: FileAssignmentRecord[],
  ): string | undefined {
    if (!shouldFilterRepositoryByMyAssignments(assignments)) {
      return listPath;
    }

    const zipPath = this.currentZipPath();
    if (zipPath && isRepositoryZipContextAllowed(zipPath, this.zipInternalPath(), assignments)) {
      return listPath;
    }

    const listingCorrection = resolveAssignmentListingPathCorrection(listPath ?? '', assignments);
    if (listingCorrection) {
      return listingCorrection;
    }

    const deepLinkTarget = this.route.snapshot.queryParamMap.get('target');
    if (deepLinkTarget) {
      const deepList = resolveRepositoryDeepLinkListPath(deepLinkTarget);
      if (deepList && isRepositoryNavigationPathAllowed(deepList, assignments)) {
        return this.resolveAssignedFolderParentListPath(deepList, assignments) ?? deepList;
      }
    }

    const norm = normalizeRepositoryExplorerPath(listPath ?? '');
    if (!norm || !isRepositoryNavigationPathAllowed(norm, assignments)) {
      const home = resolveAssignmentScopedHomePath(assignments, this.preferredAssignmentEntry());
      if (home) {
        return home;
      }
      return listPath;
    }

    return this.resolveAssignedFolderParentListPath(norm, assignments) ?? listPath;
  }

  /** Si el listado apunta a la raíz de una carpeta asignada, usa su carpeta padre. */
  private resolveAssignedFolderParentListPath(
    listPath: string,
    assignments: FileAssignmentRecord[],
  ): string | undefined {
    if (!this.pendingAssignmentEntryRedirect()) {
      return listPath;
    }
    const norm = normalizeRepositoryExplorerPath(listPath);
    for (const row of assignments) {
      if (!isAssignmentDirectoryRecord(row)) {
        continue;
      }
      const assigned = (row.file_path ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/');
      if (assigned && norm === normalizeRepositoryExplorerPath(assigned)) {
        return getParentRepositoryExplorerPath(assigned) || listPath;
      }
    }
    return listPath;
  }

  private finishRepositoryDeepLinkNavigation(): void {
    if (!this.route.snapshot.queryParamMap.get('target')) {
      return;
    }
    this.clearRepositoryDeepLinkQueryParam();
  }

  private clearRepositoryDeepLinkQueryParam(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { target: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private fetchMyFileAssignments$(force = false): Observable<FileAssignmentRecord[]> {
    if (hasFullRepositoryBrowseAccess()) {
      return of([]);
    }
    this.auth.ensureCurrentUserFromToken();
    // Preferir vault_user_id de /auth/me; el id Simappe del JWT no coincide con file_assignments.
    const userId = readPersistedVaultUserId() || vaultCurrentUserIdFromSession();
    if (!userId) {
      return of([]);
    }
    // Sin vault_user_id aún: no enviar user_id (el back filtra por JWT).
    const apiUserId = readPersistedVaultUserId() || undefined;
    return this.fileAssignmentService
      .getAssigned(apiUserId, undefined, {
        force,
        cacheNamespace: `session:${userId}`,
      })
      .pipe(
      map((rows) =>
        // No filtrar OAIS aquí: el API ya devolvió el alcance del usuario.
        // filterOaisAssignableAssignments se usa al asignar (solo DIP).
        resolveMyFileAssignmentsFromApi(rows, userId, undefined, {
          trustApiScope: true,
        }),
      ),
      catchError(() => of([])),
    );
  }

  /**
   * Agrupa disparos concurrentes (constructor, NavigationEnd, sesión) en una sola carga.
   * Evita que loadListRequestSeq cancele la respuesta buena y deje Bóveda vacía.
   */
  private scheduleScopedRepositoryBootstrap(force = true): void {
    if (hasFullRepositoryBrowseAccess()) {
      this.loadList();
      return;
    }
    this.pendingScopedBootstrapForce = this.pendingScopedBootstrapForce || force;
    if (this.scopedBootstrapDebounceTimer) {
      clearTimeout(this.scopedBootstrapDebounceTimer);
    }
    this.scopedBootstrapDebounceTimer = setTimeout(() => {
      this.scopedBootstrapDebounceTimer = null;
      const nextForce = this.pendingScopedBootstrapForce;
      this.pendingScopedBootstrapForce = false;
      this.bootstrapScopedRepositoryView(nextForce);
    }, 120);
  }

  private bootstrapScopedRepositoryView(force = true): void {
    if (hasFullRepositoryBrowseAccess()) {
      this.loadList();
      return;
    }
    if (this.scopedBootstrapInFlight) {
      this.pendingScopedBootstrapForce = this.pendingScopedBootstrapForce || force;
      return;
    }
    this.scopedBootstrapInFlight = true;
    this.bindRepositoryPersistScope();
    this.fileAssignmentService.invalidateAssignedCache();
    const seq = ++this.assignmentScopeRefreshSeq;
    this.loading.set(true);

    // Solo forzar /auth/me si aún no hay vault_user_id (evita bucle con vault-session-updated).
    const sessionReady$ = readPersistedVaultUserId()
      ? of(undefined)
      : this.operativeRoleSync.syncFromApi({ force: true });

    sessionReady$
      .pipe(
        take(1),
        switchMap(() => {
          if (seq !== this.assignmentScopeRefreshSeq) {
            return EMPTY;
          }
          this.auth.ensureCurrentUserFromToken();
          this.bindRepositoryPersistScope();
          const vaultUserId = readPersistedVaultUserId();
          const userId = vaultUserId || vaultCurrentUserIdFromSession();
          if (!userId) {
            this.scopedBootstrapInFlight = false;
            this.loading.set(false);
            this.scheduleAssignmentScopeRetry(seq, true, force);
            return EMPTY;
          }
          if (!vaultUserId && this.scopedEmptyAssignmentsRetries < 10) {
            this.scopedEmptyAssignmentsRetries += 1;
            this.scopedBootstrapInFlight = false;
            window.setTimeout(
              () => this.bootstrapScopedRepositoryView(true),
              250 * this.scopedEmptyAssignmentsRetries,
            );
            return EMPTY;
          }
          return this.fetchMyFileAssignments$(true);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (mine) => {
          if (seq !== this.assignmentScopeRefreshSeq) {
            this.scopedBootstrapInFlight = false;
            return;
          }
          this.myFileAssignments.set(mine);
          this.syncActiveAssignmentEntryPath(mine);
          this.applyScopedHomePathIfNeeded(mine, mine.length > 0);
          // Solo reintentar vacío mientras falta vault_user_id (auth/me pendiente).
          if (
            mine.length === 0 &&
            !readPersistedVaultUserId() &&
            this.scopedEmptyAssignmentsRetries < 10
          ) {
            this.scopedEmptyAssignmentsRetries += 1;
            this.scopedBootstrapInFlight = false;
            window.setTimeout(
              () => this.bootstrapScopedRepositoryView(true),
              250 * this.scopedEmptyAssignmentsRetries,
            );
            return;
          }
          this.scopedEmptyAssignmentsRetries = 0;
          this.scopedBootstrapInFlight = false;
          this.pendingScopedBootstrapForce = false;
          this.scopedSessionBootstrapCooldownUntil = Date.now() + 2000;
          this.loadList({ skipAssignmentFetch: true });
        },
        error: () => {
          if (seq !== this.assignmentScopeRefreshSeq) {
            this.scopedBootstrapInFlight = false;
            return;
          }
          this.myFileAssignments.set([]);
          this.scopedBootstrapInFlight = false;
          this.pendingScopedBootstrapForce = false;
          this.scopedSessionBootstrapCooldownUntil = Date.now() + 2000;
          this.loadList({ skipAssignmentFetch: true });
        },
      });
  }

  /** Lleva al home DIP de la asignación antes de listar (evita path heredado vacío). */
  private applyScopedHomePathIfNeeded(
    assignments: FileAssignmentRecord[],
    forceHome = false,
  ): void {
    if (!shouldFilterRepositoryByMyAssignments(assignments)) {
      return;
    }
    if (assignments.length === 0) {
      this.applyAssignmentScopedPath('');
      return;
    }
    const home = resolveAssignmentScopedHomePath(
      assignments,
      this.preferredAssignmentEntry(),
    );
    if (!home) {
      return;
    }
    const current = normalizeRepositoryExplorerPath(this.currentPath());
    const zipPath = this.currentZipPath();
    const zipOk =
      !!zipPath && isRepositoryZipContextAllowed(zipPath, this.zipInternalPath(), assignments);
    if (zipOk && !forceHome) {
      return;
    }
    if (
      forceHome ||
      !current ||
      !isRepositoryNavigationPathAllowed(current, assignments)
    ) {
      this.applyAssignmentScopedPath(home);
      this.setActiveAssignmentEntryPath(home);
    }
  }

  private refreshRepositoryAssignmentScope(reloadList = true, force = false): void {
    if (hasFullRepositoryBrowseAccess()) {
      return;
    }
    if (reloadList) {
      this.scheduleScopedRepositoryBootstrap(force);
      return;
    }
    this.bindRepositoryPersistScope();
    const seq = ++this.assignmentScopeRefreshSeq;
    this.auth.ensureCurrentUserFromToken();
    const userId = vaultCurrentUserIdFromSession();
    if (!userId) {
      this.scheduleAssignmentScopeRetry(seq, reloadList, force);
      return;
    }
    this.fetchMyFileAssignments$(force)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (mine) => {
          if (seq !== this.assignmentScopeRefreshSeq) {
            return;
          }
          this.myFileAssignments.set(mine);
          this.syncActiveAssignmentEntryPath(mine);
          this.refilterVisibleRepositoryRows();
        },
        error: () => {
          if (seq !== this.assignmentScopeRefreshSeq) {
            return;
          }
          this.myFileAssignments.set([]);
        },
      });
  }

  private scheduleAssignmentScopeRetry(
    seq: number,
    reloadList: boolean,
    force: boolean,
    attempt = 0,
  ): void {
    if (seq !== this.assignmentScopeRefreshSeq || attempt >= 12) {
      return;
    }
    if (this.assignmentScopeUserIdRetryTimer) {
      clearTimeout(this.assignmentScopeUserIdRetryTimer);
    }
    this.assignmentScopeUserIdRetryTimer = setTimeout(() => {
      this.assignmentScopeUserIdRetryTimer = null;
      if (seq !== this.assignmentScopeRefreshSeq) {
        return;
      }
      this.auth.ensureCurrentUserFromToken();
      if (readPersistedVaultUserId()) {
        this.scheduleScopedRepositoryBootstrap(force);
        return;
      }
      this.scheduleAssignmentScopeRetry(seq, reloadList, force, attempt + 1);
    }, 100 + attempt * 80);
  }

  private assignmentsScopeChanged(
    prev: FileAssignmentRecord[],
    next: FileAssignmentRecord[],
  ): boolean {
    const fingerprint = (rows: FileAssignmentRecord[]) =>
      rows
        .map((row) => row.id || row.file_path)
        .filter(Boolean)
        .sort()
        .join('|');
    return fingerprint(prev) !== fingerprint(next);
  }

  private loadMyFileAssignments(): void {
    this.refreshRepositoryAssignmentScope(false);
  }

  private applyAssignmentScopeFilter(
    items: RepositoryListItem[],
    listingPath?: string,
    options?: { ignoreListingEntryGate?: boolean },
  ): RepositoryListItem[] {
    const insideZip = Boolean(this.currentZipPath());
    const effectiveListingPath = listingPath ?? (insideZip ? this.zipInternalPath() : this.currentPath());
    /** En resultados de búsqueda el API ya acotó por `search_path`; no exigir el listado padre de cada asignación. */
    const ignoreListingEntryGate =
      options?.ignoreListingEntryGate === true ||
      (this.repositorySearchActive() && listingPath === undefined && !insideZip);
    return filterRepositoryItemsByMyAssignments(
      items,
      this.myFileAssignments(),
      insideZip,
      effectiveListingPath,
      this.currentZipPath() ?? '',
      { ignoreListingEntryGate },
    );
  }

  private refilterVisibleRepositoryRows(): void {
    const mine = this.myFileAssignments();
    if (!shouldFilterRepositoryByMyAssignments(mine)) {
      return;
    }
    const scopedCorrection = this.resolveAssignmentScopePathCorrection();
    if (scopedCorrection !== null && scopedCorrection !== this.currentPath()) {
      this.applyAssignmentScopedPath(scopedCorrection);
      this.loadList();
      return;
    }
    this.rows.update((items) => this.applyAssignmentScopeFilter(items));
    this.explorerChildCache.update((cache) => {
      const next: Record<string, RepositoryListItem[]> = {};
      for (const [path, items] of Object.entries(cache)) {
        next[path] = this.applyAssignmentScopeFilter(items, path);
      }
      return next;
    });
  }

  loadList(options?: { skipAssignmentFetch?: boolean }): void {
    const requestSeq = ++this.loadListRequestSeq;
    this.repositorySearchActive.set(false);
    this.error.set(null);
    this.accessDenied.set(false);
    this.loadError.set(null);
    this.info.set(null);
    this.loading.set(true);

    const skipAssignmentFetch = options?.skipAssignmentFetch === true;
    const currentZipPath = this.currentZipPath();
    // No listar /home, /home/egs/datavault, etc.: volver a raíz lógica de Bóveda.
    if (!currentZipPath && isOutsideRepositoryBrowseRoot(this.currentPath())) {
      this.currentPath.set('');
      this.repoStore.setCurrentPath('');
    }
    const listPath = this.currentPath() || undefined;

    const assignments$ = skipAssignmentFetch
      ? of(this.myFileAssignments())
      : this.fetchMyFileAssignments$(true);

    assignments$
      .pipe(
        switchMap((assignments) => {
          if (requestSeq !== this.loadListRequestSeq) {
            return EMPTY;
          }
          this.myFileAssignments.set(assignments);
          this.syncActiveAssignmentEntryPath(assignments);
          this.applyScopedHomePathIfNeeded(assignments, false);
          const effectiveListPath = this.resolveEffectiveListPathBeforeFetch(
            this.currentPath() || listPath,
            assignments,
          );
          if (effectiveListPath !== (this.currentPath() || listPath)) {
            this.currentPath.set(effectiveListPath ?? '');
            this.repoStore.setCurrentPath(effectiveListPath ?? '');
            const explorerPath = (effectiveListPath ?? '').trim() || EXPLORER_ROOT_PATH;
            this.selectedExplorerPath.set(explorerPath);
            this.repoStore.setSelectedExplorerPath(explorerPath);
          }
          this.pendingAssignmentEntryRedirect.set(false);
          const zipStillValid =
            !!currentZipPath &&
            isRepositoryZipContextAllowed(
              currentZipPath,
              this.zipInternalPath(),
              assignments,
            );
          const req = zipStillValid
            ? this.repositoryService.listZip(
                currentZipPath,
                sanitizeZipInternalPathForList(this.zipInternalPath()),
              )
            : this.repositoryService.list(this.currentPath() || effectiveListPath, undefined);
          return req;
        }),
        timeout(45000),
        finalize(() => {
          if (requestSeq === this.loadListRequestSeq) {
            this.loading.set(false);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res: unknown) => {
          if (requestSeq !== this.loadListRequestSeq) {
            return;
          }
          this.explorerChildCache.set({});
          this.repoStore.clearExplorerCache();
          const scopedCorrection = this.resolveAssignmentScopePathCorrection();
          if (scopedCorrection !== null && scopedCorrection !== this.currentPath()) {
            this.applyAssignmentScopedPath(scopedCorrection);
            this.loadList({ skipAssignmentFetch: true });
            return;
          }
          const activeZip = this.currentZipPath();
          const requestedZipInternal = this.zipInternalPath();
          if (activeZip && res && typeof res === 'object') {
            const serverInternal = sanitizeZipInternalPathForList(
              (res as Record<string, unknown>)['current_path'] as string | undefined,
            );
            const currentInternal = requestedZipInternal;
            // No aceptar current_path del API que salte de raíz a data/ (oculta Metadata — OAIS).
            const apiForcesDataFromRoot =
              !currentInternal && !!serverInternal && /^data(\/|$)/i.test(serverInternal);
            if (
              serverInternal !== undefined &&
              serverInternal !== currentInternal &&
              !apiForcesDataFromRoot
            ) {
              this.zipInternalPath.set(serverInternal);
              this.repoStore.setZipInternalPath(serverInternal);
            }
          }

          const applyListedRows = (listed: RepositoryListItem[]): void => {
            if (requestSeq !== this.loadListRequestSeq) {
              return;
            }
            const rows = this.applyAssignmentScopeFilter(listed);
            if (activeZip && rows.length === 0) {
              const internal = sanitizeZipInternalPathForList(this.zipInternalPath()) ?? '';
              const fallback = resolveOaisZipEmptyFolderFallback(internal, activeZip);
              const fallbackKey = `${activeZip}::${internal}->${fallback ?? ''}`;
              if (
                fallback &&
                fallback !== internal &&
                this.zipOaisEmptyFallbackKey !== fallbackKey
              ) {
                this.zipOaisEmptyFallbackKey = fallbackKey;
                this.zipInternalPath.set(fallback);
                this.repoStore.setZipInternalPath(fallback);
                this.loadList({ skipAssignmentFetch: true });
                return;
              }
            } else if (rows.length > 0) {
              this.zipOaisEmptyFallbackKey = '';
            }
            this.rows.set(rows);
            this.selectedFiles.set([]);
            if (
              !activeZip &&
              rows.length === 0 &&
              shouldFilterRepositoryByMyAssignments(this.myFileAssignments()) &&
              this.myFileAssignments().length > 0
            ) {
              this.info.set(
                'No se encontró el contenido asignado en esta ruta. Pulse Restaurar para volver al inicio de su asignación.',
              );
            } else if (activeZip && rows.length === 0) {
              this.info.set(
                'No hay archivos en esta carpeta del ZIP. En paquetes OAIS abra Metadata, manifiesto o data según corresponda.',
              );
            } else {
              this.info.set(null);
            }
            this.rehydrateExpandedExplorer();
            this.syncExplorerFolderPreviewSelection();
            this.finishRepositoryDeepLinkNavigation();
          };

          if (activeZip) {
            this.resolveOaisZipRootListing$(activeZip, requestedZipInternal, res)
              .pipe(take(1), takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (listed) => applyListedRows(listed),
                error: () => applyListedRows(this.normalizeItems(res)),
              });
            return;
          }
          applyListedRows(this.normalizeItems(res));
        },
        error: (err: unknown) => {
          if (requestSeq !== this.loadListRequestSeq) {
            return;
          }
          this.explorerChildCache.set({});
          this.repoStore.clearExplorerCache();
          if (this.handleZipListLoadError(err)) {
            return;
          }
          this.rows.set([]);
          applyVaultModuleInitialLoadErrorSignals(
            err,
            {
              moduleLabel: RepositoryAdminComponent.MODULE_LABEL,
              fallback: 'No se pudo cargar repositorio.',
            },
            {
              accessDenied: this.accessDenied,
              loadError: this.loadError,
              loadErrorTitle: this.loadErrorTitle,
            },
          );
        },
      });
  }

  applySearch(): void {
    if (this.currentZipPath()) {
      this.info.set('La búsqueda global no aplica dentro de ZIP. Sal del ZIP para buscar.');
      return;
    }
    const term = this.searchTerm().trim();
    const typeFilter = this.fileTypeFilter();
    if (term.length < 2 && typeFilter === 'all') {
      this.info.set(
        'Escribe al menos 2 caracteres del nombre o elige un tipo de archivo (PDF, ZIP, etc.).',
      );
      return;
    }
    const query = term.length >= 2 ? term : '__ext_only__';

    this.loading.set(true);
    this.error.set(null);
    this.accessDenied.set(false);
    this.loadError.set(null);
    this.info.set(null);
    this.fetchMyFileAssignments$(true)
      .pipe(
        switchMap((assignments) => {
          this.myFileAssignments.set(assignments);
          const searchOpts = {
            searchPath: this.currentPath() || undefined,
            maxResults: 200,
            searchInZips: this.searchInZips(),
            fileType: this.fileTypeFilter() === 'all' ? undefined : this.fileTypeFilter(),
          };
          return this.repositoryService.search(query, searchOpts).pipe(
            switchMap((res) => {
              if (!this.searchInZips() || !repositorySearchResponseIsEmpty(res)) {
                return of(res);
              }
              // API no indexa ZIP en este entorno: recorrer zip/list en la carpeta actual.
              return this.fallbackSearchInsideZips$(query, typeFilter).pipe(
                map((hits) =>
                  hits.length
                    ? ({ items: hits, total: hits.length } as Record<string, unknown>)
                    : res,
                ),
                catchError(() => of(res)),
              );
            }),
          );
        }),
        timeout(90000),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res: unknown) => {
          this.explorerChildCache.set({});
          this.repoStore.clearExplorerCache();
          this.repositorySearchActive.set(true);
          const rows = this.applyAssignmentScopeFilter(this.normalizeItems(res), undefined, {
            ignoreListingEntryGate: true,
          });
          this.rows.set(rows);
          this.selectedFiles.set([]);
          this.explorerExpandedPaths.set(new Set());
          this.repoStore.setExplorerExpandedPaths([]);
          this.syncExplorerSelectionAfterSearch(rows);
          if (!rows.length) {
            const typeLabel = typeFilter === 'all' ? '' : this.repositoryFileTypeLabel(typeFilter);
            const typeHint =
              typeFilter === 'text' ? ' (.txt, .log, .csv, .md)' : '';
            this.info.set(
              typeLabel
                ? `No se encontraron archivos de tipo ${typeLabel}${typeHint} en las rutas buscables. Prueba con un nombre (ej. Colombia) o navega hasta la carpeta del proyecto.`
                : 'No hay archivos para la ruta o consulta actual.',
            );
          }
        },
        error: (err: unknown) => {
          this.explorerChildCache.set({});
          this.repoStore.clearExplorerCache();
          this.rows.set([]);
          this.repositorySearchActive.set(false);
          this.info.set(null);
          applyVaultModuleInitialLoadErrorSignals(
            err,
            {
              moduleLabel: RepositoryAdminComponent.MODULE_LABEL,
              fallback: 'No se pudo buscar en repositorio. Revisa la conexion con el API o reinicia el backend.',
            },
            {
              accessDenied: this.accessDenied,
              loadError: this.loadError,
              loadErrorTitle: this.loadErrorTitle,
            },
          );
        },
      });
  }

  /**
   * Fallback cliente: lista `.zip` en la carpeta actual y busca nombre/tipo con `zip/list`.
   * Cubre el caso en que `search_in_zips=true` del API no devuelve hits.
   */
  private fallbackSearchInsideZips$(
    query: string,
    fileType: string,
  ): Observable<RepositoryListItem[]> {
    const listingPath = this.currentPath() || '';
    return this.repositoryService.list(listingPath || undefined).pipe(
      map((listing) => collectZipArchivePathsFromListing(listing, listingPath)),
      switchMap((zipPaths) => {
        if (!zipPaths.length) {
          return of([] as RepositoryListItem[]);
        }
        return searchInsideZipArchives$(
          zipPaths,
          {
            query,
            fileType: fileType || 'all',
            maxResults: 200,
            maxZips: 25,
            maxDepth: 14,
          },
          (zipPath, internalPath) => this.repositoryService.listZip(zipPath, internalPath),
        ).pipe(map((hits) => zipClientSearchHitsAsListItems(hits)));
      }),
      catchError(() => of([] as RepositoryListItem[])),
    );
  }

  /** Sale de resultados de búsqueda o sube de carpeta / ZIP. */
  onTableNavBack(): void {
    if (this.repositorySearchActive()) {
      this.restoreRoot();
      return;
    }
    this.goUp();
  }

  canTableNavBack(): boolean {
    const ctx = {
      repositorySearchActive: this.repositorySearchActive(),
      currentZipPath: this.currentZipPath(),
      currentPath: this.currentPath(),
      zipInternalPath: this.zipInternalPath(),
    };
    const mine = this.myFileAssignments();
    if (shouldFilterRepositoryByMyAssignments(mine)) {
      return canRepositoryTableNavBackScoped(ctx, mine);
    }
    return canRepositoryTableNavBack(ctx);
  }

  tableContextLabel(): string {
    if (this.repositorySearchActive()) {
      const parts = ['Resultados de búsqueda'];
      const term = this.searchTerm().trim();
      if (term.length >= 2) {
        parts.push(`"${term}"`);
      }
      const type = this.fileTypeFilter();
      if (type !== 'all') {
        parts.push(`tipo ${this.repositoryFileTypeLabel(type)}`);
      }
      const path = (this.currentPath() || '').trim();
      if (path) {
        parts.push(`en ${formatRepositoryContextPath(path)}`);
      }
      return parts.join(' · ');
    }
    if (this.currentZipPath()) {
      const inner = sanitizeZipInternalPathForList(this.zipInternalPath());
      return `ZIP ${formatRepositoryContextPath(this.currentZipPath()!)} / ${inner || '(raíz)'}`;
    }
    return formatRepositoryContextPath(this.currentPath());
  }

  goUp(): void {
    if (this.currentZipPath()) {
      const mine = this.myFileAssignments();
      const zipInternal = this.zipInternalPath();
      if (shouldFilterRepositoryByMyAssignments(mine)) {
        if (zipInternal) {
          const scopedParent = resolveRepositoryZipGoUpScoped(
            this.currentZipPath()!,
            zipInternal,
            mine,
          );
          if (scopedParent !== null) {
            this.zipInternalPath.set(scopedParent);
            this.repoStore.setZipInternalPath(scopedParent);
            this.loadList();
            return;
          }
          return;
        }
        const zipParent = getParentRepositoryExplorerPath(this.currentZipPath()!);
        if (zipParent && isRepositoryNavigationPathAllowed(zipParent, mine)) {
          this.leaveZip();
        }
        return;
      }
      const zipGoUp = resolveRepositoryZipGoUp(this.zipInternalPath());
      if (zipGoUp.kind === 'leaveZip') {
        this.leaveZip();
        return;
      }
      this.zipInternalPath.set(zipGoUp.nextInternalPath);
      this.repoStore.setZipInternalPath(zipGoUp.nextInternalPath);
      this.loadList();
      return;
    }

    const mine = this.myFileAssignments();
    // Si el path actual ya está fuera de /Repositorio (/home, datavault…), Volver → raíz.
    if (
      !shouldFilterRepositoryByMyAssignments(mine) &&
      isOutsideRepositoryBrowseRoot(this.currentPath())
    ) {
      this.currentPath.set('');
      this.repoStore.setCurrentPath('');
      this.selectedExplorerPath.set(EXPLORER_ROOT_PATH);
      this.repoStore.setSelectedExplorerPath(EXPLORER_ROOT_PATH);
      this.loadList();
      return;
    }
    const parent = shouldFilterRepositoryByMyAssignments(mine)
      ? resolveRepositoryFolderGoUpScoped(this.currentPath(), mine)
      : resolveRepositoryFolderGoUp(this.currentPath());
    if (parent === null) {
      return;
    }
    const nextPath = clampRepositoryBrowsePath(parent);
    this.currentPath.set(nextPath);
    this.repoStore.setCurrentPath(nextPath);
    const explorerPath = nextPath.trim() || EXPLORER_ROOT_PATH;
    this.selectedExplorerPath.set(explorerPath);
    this.repoStore.setSelectedExplorerPath(explorerPath);
    this.loadList();
  }

  restoreRoot(): void {
    this.repositorySearchActive.set(false);
    this.info.set(null);
    this.closeExplorerZip();
    this.currentZipPath.set(null);
    this.repoStore.setCurrentZipPath(null);
    this.zipInternalPath.set('');
    this.repoStore.setZipInternalPath('');
    // Limpiar campos de filtro para todos los roles (antes fallaba en operativos por early-return).
    this.searchTerm.set('');
    this.repoStore.setSearchTerm('');
    this.fileTypeFilter.set('all');
    this.repoStore.setFileTypeFilter('all');
    this.searchInZips.set(false);
    this.repoStore.setSearchInZips(false);
    this.selectedExplorerPath.set(null);
    this.repoStore.setSelectedExplorerPath(null);
    // Roles acotados: volver al home de asignación y reconsultar (sin ver todo el repo).
    if (!hasFullRepositoryBrowseAccess()) {
      this.scheduleScopedRepositoryBootstrap(true);
      return;
    }
    this.currentPath.set('');
    this.repoStore.setCurrentPath('');
    this.setActiveAssignmentEntryPath('');
    this.loadList();
  }

  leaveZip(): void {
    this.currentZipPath.set(null);
    this.repoStore.setCurrentZipPath(null);
    this.zipInternalPath.set('');
    this.repoStore.setZipInternalPath('');
    this.loadList();
  }

  onAction(ev: ActionEvent<RepositoryListItem>): void {
    if (ev.action === 'open') this.openRow(ev.data);
    if (ev.action === 'download') {
      if (this.isFolder(ev.data)) this.downloadFolder(ev.data);
      else this.downloadOne(ev.data);
    }
    if (ev.action === 'preview') this.previewRow(ev.data);
    if (ev.action === 'assign') this.openAssignDialogFromRow(ev.data);
  }

  onExplorerAssignFile(node: FileNode): void {
    this.openAssignDialogFromNode(node);
  }

  private resolveExplorerZipArchivePath(node: FileNode): string {
    const raw = (node.path ?? '').trim().replace(/\/+/g, '/');
    if (raw.includes('/')) {
      return raw;
    }
    const base = this.resolveExplorerAssignBasePath();
    const name = (node.name ?? raw).trim();
    if (base && name) {
      return `${base}/${name}`.replace(/\/+/g, '/');
    }
    return name || raw;
  }

  private resolveAssignNodePath(node: FileNode): string {
    const row = this.fileNodeToRepositoryItem(node);
    const raw = (node.path ?? '').trim().replace(/\/+/g, '/');
    if (this.explorerZipPath() && !this.isZipRow(row)) {
      return this.resolveRepositoryAssignFilePath(raw, node.type === 'directory');
    }
    if (raw.includes('/')) {
      return this.resolveRepositoryAssignFilePath(raw, node.type === 'directory');
    }
    const base = this.resolveExplorerAssignBasePath();
    const name = (node.name ?? raw).trim();
    if (base && name) {
      return this.resolveRepositoryAssignFilePath(`${base}/${name}`.replace(/\/+/g, '/'), node.type === 'directory');
    }
    return this.resolveRepositoryAssignFilePath(name || raw, node.type === 'directory');
  }

  /** Ruta OAIS (o virtual `archivo.zip/interno`) enviada al API de asignación. */
  private resolveRepositoryAssignFilePath(path: string, isDirectory = false): string {
    const raw = (path ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/');
    if (!raw) {
      return '';
    }
    const tableZip = (this.currentZipPath() ?? '').trim();
    const explorerZip = (this.explorerZipPath() ?? '').trim();
    const zipArchive = tableZip || explorerZip;
    if (zipArchive) {
      const archive = normalizeRepositoryExplorerPath(zipArchive);
      if (this.isZipAssignPath(archive)) {
        if (this.isZipAssignPath(raw) && normalizeRepositoryExplorerPath(raw) === archive) {
          return archive;
        }
        const zipInternal = tableZip ? this.zipInternalPath() : this.explorerZipInternalPath();
        const relative = normalizeZipInternalPath(raw);
        const fullInternal = relative.includes('/')
          ? relative
          : normalizeZipInternalPath([zipInternal, relative].filter(Boolean).join('/'));
        if (fullInternal) {
          return buildZipVirtualAssignmentPath(archive, fullInternal);
        }
        return archive;
      }
    }
    if (/^\/home(\/|$)/i.test(raw) || splitZipVirtualAssignmentPath(raw)) {
      return normalizeRepositoryExplorerPath(raw);
    }
    return normalizeRepositoryExplorerPath(resolveRepositoryItemPath(raw, this.currentPath()));
  }

  /** Carpeta del repositorio usada como prefijo al asignar (nunca la ruta de un .zip abierto). */
  private resolveExplorerAssignBasePath(): string {
    const selected = (this.selectedExplorerPath() ?? '').trim().replace(/\/$/, '');
    if (!selected || selected === EXPLORER_ROOT_PATH) {
      return '';
    }
    if (this.looksLikeRepositoryFilePath(selected)) {
      const parts = selected.split('/').filter(Boolean);
      parts.pop();
      return parts.join('/');
    }
    return selected;
  }

  private looksLikeRepositoryFilePath(path: string): boolean {
    const base = path.split('/').filter(Boolean).pop() ?? '';
    return /\.[a-z0-9]{2,8}$/i.test(base);
  }

  openAssignDialogFromRow(row: RepositoryListItem): void {
    const path = this.resolveRepositoryAssignFilePath(
      this.getPath(row) || row.path || row.name || '',
      this.isFolder(row) && !this.isZipRow(row),
    );
    if (!path) return;
    this.openAssignDialogWithItems([
      {
        path,
        name: row.name || path,
        isDirectory: this.isFolder(row) && !this.isZipRow(row),
      },
    ]);
  }

  openAssignDialogFromNode(node: FileNode): void {
    const path = this.resolveAssignNodePath(node);
    if (!path) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Asignar',
        message: 'No se pudo determinar la ruta del archivo para asignar.',
      });
      return;
    }
    const row = this.fileNodeToRepositoryItem(node);
    this.openAssignDialogWithItems([
      {
        path,
        name: node.name || path,
        isDirectory: node.type === 'directory' && !this.isZipRow(row),
      },
    ]);
  }

  openAssignExplorerFolder(): void {
    const node = this.explorerAssignableFolder();
    if (!node) {
      return;
    }
    this.openAssignDialogWithItems([
      {
        path: node.path,
        name: node.name || node.path,
        isDirectory: true,
      },
    ]);
  }

  openAssignSelection(): void {
    const paths = this.selectedFiles();
    if (paths.length === 0) {
      return;
    }
    const items: AssignTargetItem[] = paths.map((path) => this.buildAssignItemFromPath(path));
    this.openAssignDialogWithItems(items);
  }

  removeAssignTargetItem(path: string): void {
    const next = this.assignTargetItems().filter((i) => i.path !== path);
    if (next.length === 0) {
      this.closeAssignDialog();
      return;
    }
    this.assignTargetItems.set(next);
  }

  getAssignLocationHint(item: AssignTargetItem): string {
    if (this.isSuperAdmin()) {
      return item.path;
    }
    const parts = item.path.split('/').filter(Boolean);
    if (parts.length <= 1) {
      return `Ubicación: ${item.name}`;
    }
    return `Ubicación: ${parts.slice(-3).join('/')}`;
  }

  private openAssignDialogWithItems(items: AssignTargetItem[]): void {
    if (!this.canAssignFiles()) {
      return;
    }
    this.refreshCanAssignFiles();
    this.auth.syncVaultTenantFromAccessToken();
    this.syncRepositoryTenantFromSession();
    this.hydrateAssignTenantScope();
    const { paths, items: normalized } = buildAssignTargetItems(items);
    if (paths.length === 0) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Asignar',
        message: 'La ruta seleccionada no es válida para asignar.',
      });
      return;
    }
    const blocked = normalized.find((item) => !isDipAssignablePath(item.path));
    if (blocked) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Asignar',
        message:
          getOaisAssignmentBlockReason(blocked.path) ??
          'Según OAIS, solo el contenido bajo DIP puede asignarse a consulta.',
      });
      return;
    }
    this.assignTargetItems.set(normalized);
    this.assignUserIds.set([]);
    this.assignExpiration.set('permanent');
    this.assignUserSearch.set('');
    this.assignDialogOpen.set(true);
    this.loadUsersForAssign();
    this.cdr.detectChanges();
  }

  private buildAssignItemFromPath(path: string): AssignTargetItem {
    const row = this.rows().find((r) => this.getPath(r) === path);
    if (row) {
      const resolved = this.resolveRepositoryAssignFilePath(
        path,
        this.isFolder(row) && !this.isZipRow(row),
      );
      return {
        path: resolved,
        name: row.name || resolved,
        isDirectory: this.isFolder(row) && !this.isZipRow(row),
      };
    }
    const node = this.findExplorerNodeByPath(this.explorerFileTree(), path);
    if (node) {
      const resolved = this.resolveAssignNodePath(node);
      return {
        path: resolved,
        name: node.name || resolved,
        isDirectory: node.type === 'directory',
      };
    }
    const name = path.split('/').filter(Boolean).pop() || path;
    return {
      path: this.resolveRepositoryAssignFilePath(path, false),
      name,
      isDirectory: false,
    };
  }

  private findExplorerNodeByPath(nodes: FileNode[], path: string): FileNode | null {
    for (const node of nodes) {
      if (node.path === path) {
        return node;
      }
      if (node.children?.length) {
        const found = this.findExplorerNodeByPath(node.children, path);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  closeAssignDialog(): void {
    this.assignDialogOpen.set(false);
    this.assignTargetItems.set([]);
    this.assignUserIds.set([]);
    this.assignExpiration.set('permanent');
    this.assignUserSearch.set('');
    this.assignUserPicker.set('');
    this.assignUsersLoadHint.set(null);
    this.assignScopeTenantId.set('');
    this.lastAssignUsersRaw = [];
  }

  onAssignUserSearchChange(value: string): void {
    this.assignUserSearch.set(value ?? '');
  }

  onAssignUserPickerChange(userId: string): void {
    const id = (userId ?? '').trim();
    this.assignUserPicker.set(id);
    if (!id || this.assignUserIds().includes(id)) {
      return;
    }
    this.assignUserIds.set([...this.assignUserIds(), id]);
    this.syncTenantFromSelectedAssignUsers();
    queueMicrotask(() => this.assignUserPicker.set(''));
  }

  removeSelectedAssignUser(userId: string): void {
    this.assignUserIds.set(this.assignUserIds().filter((x) => x !== userId));
    this.syncTenantFromSelectedAssignUsers();
  }

  onAssignExpirationChange(value: string): void {
    this.assignExpiration.set(value || 'permanent');
  }

  isAssignUserSelected(userId: string): boolean {
    return this.assignUserIds().includes(userId);
  }

  toggleAssignUser(userId: string, checked: boolean): void {
    const set = new Set(this.assignUserIds());
    if (checked) set.add(userId);
    else set.delete(userId);
    this.assignUserIds.set(Array.from(set));
    this.syncTenantFromSelectedAssignUsers();
  }

  canSubmitAssign(): boolean {
    return this.assignUserIds().length > 0;
  }

  saveAssignment(): void {
    this.refreshCanAssignFiles();
    this.auth.syncVaultTenantFromAccessToken();
    this.syncRepositoryTenantFromSession();
    this.syncTenantFromSelectedAssignUsers();

    if (!this.canSubmitAssign()) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Asignar',
        message: 'Selecciona al menos un usuario.',
      });
      return;
    }

    const isSuper = this.isSuperAdmin() || this.isSuperAdminUser();
    const usersPool = [...this.lastAssignUsersRaw, ...this.availableUsersForAssign()];

    this.vaultTenantScope
      .resolveForAssign$({
        assignScopeTenantId: this.assignScopeTenantId(),
        authMeTenantId: this.authMeTenantId(),
        assignUsers: usersPool,
        selectedUserIds: this.assignUserIds(),
        isSuperAdmin: isSuper,
      })
      .pipe(take(1))
      .subscribe((tid) => {
        if (!tid) {
          if (this.assignUserIds().length > 0) {
            this.vaultAppToast.show({
              type: 'error',
              title: 'Asignar',
              message:
                'No se pudo obtener la empresa (tenant) de la sesión Vault. Vuelve a entrar a Vault tras seleccionar empresa en Nebula o contacta al administrador.',
            });
          } else {
            this.vaultAppToast.show({
              type: 'error',
              title: 'Asignar',
              message: 'Selecciona al menos un usuario para asignar.',
            });
          }
          return;
        }
        this.applyResolvedAssignTenantId(tid);
        this.commitSaveAssignment(tid);
      });
  }

  private commitSaveAssignment(tenantId: string): void {
    persistActiveTenantId(tenantId, this.serverContext);
    const paths = this.assignTargetItems().map((i) => i.path);
    if (paths.length === 0) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Asignar',
        message: 'Selecciona al menos un archivo o carpeta para asignar.',
      });
      return;
    }
    const blocked = paths.find((path) => !isDipAssignablePath(path));
    if (blocked) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Asignar',
        message:
          getOaisAssignmentBlockReason(blocked) ??
          'Según OAIS, solo el contenido bajo DIP puede asignarse a consulta.',
      });
      return;
    }
    if (!this.canSubmitAssign()) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Asignar',
        message: 'Selecciona al menos un usuario.',
      });
      return;
    }

    const expiration = this.assignExpiration();
    const expirationHours =
      expiration === 'permanent' ? undefined : Number.parseInt(expiration, 10);
    const assignAsDirectory =
      this.assignTargetIsDirectory() &&
      !paths.some((p) => this.isZipAssignPath(p) && !splitZipVirtualAssignmentPath(p));

    this.assignSaving.set(true);
    this.error.set(null);
    this.fileAssignmentService
      .assign({
        tenant_id: tenantId,
        file_paths: paths,
        is_directory: assignAsDirectory,
        user_ids: this.assignUserIds(),
        expiration_hours: Number.isFinite(expirationHours) ? expirationHours : undefined,
      })
      .pipe(finalize(() => this.assignSaving.set(false)))
      .subscribe({
        next: () => {
          const count = paths.length;
          this.vaultAppToast.show({
            type: 'success',
            title: 'Asignación realizada',
            message:
              count > 1
                ? `${count} elementos asignados correctamente.`
                : 'El elemento se asignó correctamente.',
          });
          this.selectedFiles.set([]);
          this.closeAssignDialog();
          this.refreshRepositoryAssignmentScope(true, true);
          notifyVaultFileAssignmentsChanged();
        },
        error: (err: unknown) => {
          const msg = toModuleAccessErrorMessage(err, {
            moduleLabel: RepositoryAdminComponent.MODULE_LABEL,
            fallback: 'No se pudieron asignar los archivos o carpetas.',
          });
          this.vaultAppToast.show({
            type: 'error',
            title: 'Error al asignar',
            message: msg,
          });
        },
      });
  }

  /** Etiqueta checkbox usuario + rol (util compartido). */
  readonly formatAssignUserLabel = buildAssignUserCheckboxLabel;

  private loadUsersForAssign(): void {
    this.syncRepositoryTenantFromSession();
    this.assignUsersLoadHint.set(null);
    this.assignUsersLoading.set(true);
    const requestSeq = ++this.assignUsersRequestSeq;

    forkJoin({
      users: this.fileAssignmentService.listUsersForAssign(this.isSuperAdmin()),
      me: this.profileService.getMe().pipe(catchError(() => of(null))),
      myTenants: this.profileService.getMyTenants().pipe(catchError(() => of(null))),
    })
      .pipe(finalize(() => this.assignUsersLoading.set(false)))
      .subscribe({
        next: ({ users, me, myTenants }) => {
          if (requestSeq !== this.assignUsersRequestSeq) {
            return;
          }
          this.lastAssignUsersRaw = users;
          const isSuper = this.isSuperAdmin();
          const fromMe = tenantIdFromAuthMeResponse(me);
          const fromMyTenants = tenantIdFromMyTenantsResponse(myTenants);
          const fromUsers = tenantIdFromAssignUsers(users, []);
          const sessionTenant = firstNonEmptyTenantId(
            fromMyTenants,
            fromMe,
            this.authMeTenantId().trim(),
            this.vaultTenantScope.apiTenantId(),
          );
          const scopeTenantId = isSuper ? '' : sessionTenant || fromUsers || '';
          if (!isSuper && sessionTenant) {
            this.assignScopeTenantId.set(sessionTenant);
            this.applyResolvedAssignTenantId(sessionTenant);
          } else if (!isSuper && fromUsers) {
            this.assignScopeTenantId.set(fromUsers);
            this.applyResolvedAssignTenantId(fromUsers);
          }

          let mapped = mapUsersForAssignDialog(users, scopeTenantId, {
            superAdminFallbackAll: isSuper,
            apiAlreadyTenantScoped: !isSuper,
          });
          if (mapped.length === 0 && users.length > 0 && !isSuper) {
            mapped = mapUsersForAssignDialog(users, scopeTenantId, {
              superAdminFallbackAll: false,
              apiAlreadyTenantScoped: true,
            });
          }
          this.availableUsersForAssign.set(mapped);

          const afterUsersTenant =
            tenantIdFromAssignUsers(mapped, this.assignUserIds()) ||
            tenantIdFromAssignUsers(users, this.assignUserIds());
          if (afterUsersTenant) {
            this.assignScopeTenantId.set(afterUsersTenant);
            this.applyResolvedAssignTenantId(afterUsersTenant);
          }

          if (mapped.length === 0) {
            this.assignUsersLoadHint.set(
              users.length === 0
                ? 'No hay usuarios registrados en el sistema.'
                : scopeTenantId
                  ? 'No hay usuarios asignables para la empresa de tu sesión.'
                  : 'No se pudo determinar la empresa de la sesión Vault.',
            );
          }
        },
        error: () => {
          if (requestSeq !== this.assignUsersRequestSeq) {
            return;
          }
          this.availableUsersForAssign.set([]);
          this.assignUsersLoadHint.set('No se pudo cargar la lista de usuarios.');
          this.vaultAppToast.show({
            type: 'error',
            title: 'Asignar',
            message: 'No se pudo cargar la lista de usuarios para asignar.',
          });
        },
      });
  }

  onRowClick(row: RepositoryListItem): void {
    if (this.isFolder(row)) {
      this.openRow(row);
      return;
    }
    if (this.isZipRow(row)) {
      if (this.isRowUnderAip(row)) {
        this.vaultAppToast.show({
          type: 'info',
          title: 'AIP',
          message:
            'Según OAIS, el contenido AIP es de archivo interno y no se abre para consulta. Use la carpeta DIP del proyecto.',
        });
        return;
      }
      if (this.viewMode() === 'explorer') {
        this.openExplorerZip(this.repositoryRowToFileNode(row));
        return;
      }
      this.openRow(row);
      return;
    }
    this.previewRow(row);
  }

  onDownloadSelectedClick(): void {
    this.downloadSelected();
  }

  onTableSort(ev: SortChangeEvent<RepositoryListItem>): void {
    this.tableSortColumn.set(ev.column);
    this.tableSortDirection.set(ev.direction);
    this.repoStore.setTableState({
      sortColumn: ev.column,
      sortDirection: ev.direction,
      pageIndex: this.tablePageIndex(),
      pageSize: this.tablePageSize(),
    });
  }

  onTablePageChange(ev: { pageIndex: number; pageSize: number }): void {
    this.tablePageSize.set(ev.pageSize);
    this.tablePageIndex.set(ev.pageIndex);
    this.repoStore.setTableState({
      pageIndex: ev.pageIndex,
      pageSize: ev.pageSize,
      sortColumn: this.tableSortColumn(),
      sortDirection: this.tableSortDirection(),
    });
  }

  toggleSelection(path: string, checked: boolean): void {
    const set = new Set(this.selectedFiles());
    if (checked) set.add(path);
    else set.delete(path);
    this.selectedFiles.set(Array.from(set));
  }

  isSelected(path: string): boolean {
    return this.selectedFiles().includes(path);
  }

  closePreview(): void {
    const url = this.previewSrcUrl();
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    this.previewOpen.set(false);
    this.previewSrcUrl.set('');
    this.previewSrcData.set(null);
    this.previewText.set('');
    this.previewMimeType.set('');
    this.previewTitle.set('');
    this.previewDownloadName.set('');
    this.previewUnsupportedMessage.set('');
    this.previewLoading.set(false);
    this.previewError.set('');
    this.selectedExplorerFilePath.set(null);
    this.restoreExplorerFolderAfterPreview();
  }

  /** Tras cerrar la vista previa, volver a la carpeta activa (no dejar el panel en raíz vacía). */
  private restoreExplorerFolderAfterPreview(): void {
    if (this.viewMode() !== 'explorer' || this.explorerZipPath()) {
      return;
    }
    const folderPath =
      this.explorerPanelFolderPath() ??
      this.resolveExplorerPanelFolderPath(this.selectedExplorerPath() ?? '');
    if (!folderPath) {
      return;
    }
    this.explorerPanelFolderPath.set(folderPath);
    this.selectedExplorerPath.set(folderPath);
    this.repoStore.setSelectedExplorerPath(folderPath);
  }

  /** Carpeta del panel derecho a partir de la selección actual o de la ruta del archivo. */
  private resolveExplorerPanelFolderPath(fileOrFolderPath: string): string | null {
    const current = (fileOrFolderPath ?? '').trim();
    if (!current) {
      return null;
    }
    if (current === EXPLORER_ROOT_PATH) {
      return EXPLORER_ROOT_PATH;
    }
    if (!this.looksLikeRepositoryFilePath(current)) {
      return current;
    }
    const stored = (this.explorerPanelFolderPath() ?? '').trim();
    if (stored && stored !== EXPLORER_ROOT_PATH && !this.looksLikeRepositoryFilePath(stored)) {
      return stored;
    }
    const parts = current.split('/').filter(Boolean);
    if (parts.length <= 1) {
      return EXPLORER_ROOT_PATH;
    }
    parts.pop();
    return parts.join('/');
  }

  private handleZipListLoadError(err: unknown): boolean {
    if (!this.currentZipPath()) {
      return false;
    }
    const msg = toModuleAccessErrorMessage(err, {
      moduleLabel: RepositoryAdminComponent.MODULE_LABEL,
      fallback: 'No se pudo abrir la carpeta dentro del ZIP.',
    });
    this.error.set(msg);
    this.loadError.set(null);
    this.loadErrorTitle.set('');
    this.accessDenied.set(false);

    const failedInternal = this.zipInternalPath();
    if (!failedInternal || this.zipListRecoverInFlight) {
      this.rows.set([]);
      return true;
    }

    this.zipListRecoverInFlight = true;
    const parent = getParentZipInternalPath(failedInternal);
    this.zipInternalPath.set(parent);
    this.repoStore.setZipInternalPath(parent);
    this.loadList();
    this.zipListRecoverInFlight = false;
    return true;
  }

  private openRow(row: RepositoryListItem): void {
    const rowPath = this.getFullRepositoryRowPath(row);
    if (!rowPath) return;

    if (this.isZipRow(row) && this.isRowUnderAip(row)) {
      this.vaultAppToast.show({
        type: 'info',
        title: 'AIP',
        message:
          'Según OAIS, el contenido AIP es de archivo interno y no se abre para consulta. Use la carpeta DIP del proyecto.',
      });
      return;
    }

    const mine = this.myFileAssignments();
    if (shouldFilterRepositoryByMyAssignments(mine)) {
      if (this.isZipRow(row)) {
        const zipArchivePath = this.currentZipPath() || rowPath;
        if (!isRepositoryZipContextAllowed(zipArchivePath, '', mine)) {
          this.info.set('No tienes acceso a esa ubicación del repositorio.');
          return;
        }
      } else if (this.currentZipPath()) {
        if (!isRepositoryZipContextAllowed(this.currentZipPath()!, rowPath, mine)) {
          this.info.set('No tienes acceso a esa ubicación del repositorio.');
          return;
        }
      } else if (!isRepositoryNavigationPathAllowed(rowPath, mine)) {
        this.info.set('No tienes acceso a esa ubicación del repositorio.');
        return;
      }
    }

    if (this.currentZipPath()) {
      const segment = resolveZipInternalRowSegment(row);
      const current = sanitizeZipInternalPathForList(this.zipInternalPath()) ?? '';
      // Navegar al segmento exacto: no saltar a data/<proyecto> (oculta Metadata/manifiesto — OAIS C4).
      const next =
        segment.includes('/') || !current
          ? segment
          : resolveZipInternalNavigatePath(current, segment);
      this.zipOaisEmptyFallbackKey = '';
      this.zipInternalPath.set(next);
      this.repoStore.setZipInternalPath(next);
      this.loadList();
      return;
    }

    if (this.isZipRow(row)) {
      const zipArchivePath = this.currentZipPath() || rowPath;
      this.currentZipPath.set(zipArchivePath);
      this.repoStore.setCurrentZipPath(zipArchivePath);
      this.zipInternalPath.set('');
      this.repoStore.setZipInternalPath('');
      this.loadList();
      return;
    }
    if (isOutsideRepositoryBrowseRoot(rowPath)) {
      this.info.set('No se puede navegar fuera del repositorio documental.');
      return;
    }
    const nextPath = clampRepositoryBrowsePath(rowPath) || rowPath;
    this.currentPath.set(nextPath);
    this.repoStore.setCurrentPath(nextPath);
    this.loadList();
  }

  private previewRow(row: RepositoryListItem, zipArchivePath: string | null = null): void {
    const zipHit = zipArchivePath ? null : this.resolveZipSearchHit(row);
    const rowPath = zipHit
      ? zipHit.internalPath
      : this.resolveRepositoryDownloadPath(row);
    if (!rowPath) return;
    this.previewOpen.set(true);
    this.previewTitle.set(row.name || 'Vista previa');
    this.previewDownloadName.set(row.name || 'archivo');
    this.previewMimeType.set('');
    this.previewSrcUrl.set('');
    this.previewSrcData.set(null);
    this.previewText.set('');
    this.previewUnsupportedMessage.set('');
    this.previewError.set('');
    this.previewLoading.set(true);
    this.loadingAction.set(true);
    const currentZipPath = zipArchivePath ?? zipHit?.zipPath ?? this.currentZipPath();
    const req = currentZipPath
      ? this.repositoryService.downloadFromZip(currentZipPath, rowPath)
      : this.repositoryService.download(rowPath);

    req
      .pipe(timeout(120_000))
      .subscribe({
        next: (blob: Blob) => {
          void this.applyPreviewBlob(blob, row.name || 'archivo');
        },
        error: (err: unknown) => {
          const isTimeout =
            err &&
            typeof err === 'object' &&
            'name' in err &&
            (err as { name?: string }).name === 'TimeoutError';
          const msg = isTimeout
            ? 'Tiempo de espera agotado al descargar el archivo para la vista previa.'
            : toModuleAccessErrorMessage(err, {
                moduleLabel: 'Bóveda Digital',
                fallback: 'No se pudo abrir preview.',
              });
          this.previewError.set(msg);
          this.vaultAppToast.show({
            type: 'error',
            title: 'Vista previa',
            message: msg,
          });
          this.previewLoading.set(false);
          this.loadingAction.set(false);
        },
      });
  }

  /**
   * RxJS ejecuta finalize() antes de terminar callbacks async en subscribe({ next: async ... }),
   * lo que quitaba “Cargando…” con el modal aún vacío. Todo el trabajo async va aquí y solo al
   * final se apaga previewLoading / loadingAction.
   */
  private async applyPreviewBlob(blob: Blob, fallbackName: string): Promise<void> {
    try {
      if (!blob || blob.size === 0) {
        this.previewError.set('El archivo descargado está vacío o no se pudo leer.');
        return;
      }
      const name = fallbackName || 'archivo';
      const mime = blob.type || detectMimeFromName(name);
      this.previewMimeType.set(mime);
      this.previewTitle.set(name);
      this.previewDownloadName.set(name);
      this.previewUnsupportedMessage.set('');
      this.previewError.set('');

      // PDF y Office antes que “texto”: `openxml` contiene la subcadena `xml` y un .xlsx se confundiría con XML.
      if (isPdfMime(mime, name)) {
        const buf = await blob.arrayBuffer();
        this.previewSrcUrl.set(URL.createObjectURL(new Blob([buf], { type: mime || 'application/pdf' })));
        this.previewSrcData.set(new Uint8Array(buf));
        this.previewText.set('');
      } else if (isOfficeMime(mime, name)) {
        this.previewSrcUrl.set(URL.createObjectURL(blob));
        this.previewSrcData.set(null);
        this.previewText.set('');
        this.previewUnsupportedMessage.set(
          'La vista previa de Excel, Word o PowerPoint no está disponible en el navegador. Descarga el archivo y ábrelo en la aplicación de escritorio.',
        );
      } else if (isTextLikeMime(mime, name)) {
        this.previewText.set(await blob.text());
        this.previewSrcUrl.set('');
        this.previewSrcData.set(null);
      } else {
        this.previewSrcUrl.set(URL.createObjectURL(blob));
        this.previewSrcData.set(null);
        this.previewText.set('');
      }
      this.previewOpen.set(true);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : 'No se pudo preparar la vista previa.';
      this.previewError.set(msg);
      this.vaultAppToast.show({
        type: 'error',
        title: 'Vista previa',
        message: msg,
      });
    } finally {
      this.previewLoading.set(false);
      this.loadingAction.set(false);
    }
  }

  private downloadOne(row: RepositoryListItem, zipArchivePath: string | null = null): void {
    const zipHit = zipArchivePath ? null : this.resolveZipSearchHit(row);
    const rowPath = zipHit
      ? zipHit.internalPath
      : this.resolveRepositoryDownloadPath(row);
    if (!rowPath) return;
    this.loadingAction.set(true);
    const currentZipPath = zipArchivePath ?? zipHit?.zipPath ?? this.currentZipPath();
    const oaisZipOnDisk =
      !currentZipPath &&
      this.isZipRow(row) &&
      this.isOaisPackageLocation(rowPath || this.currentPath());

    const req = currentZipPath
      ? this.repositoryService.downloadFromZip(currentZipPath, rowPath)
      : oaisZipOnDisk
        ? this.buildFullOaisZipArchiveDownload$(rowPath)
        : this.repositoryService.download(rowPath);
    req
      .pipe(finalize(() => this.loadingAction.set(false)))
      .subscribe({
        next: (blob: Blob) => {
          this.triggerDownload(blob, this.resolveDownloadFileName(row, oaisZipOnDisk));
        },
        error: (err: unknown) => {
          this.vaultAppToast.show({
            type: 'error',
            title: 'Descarga',
            message: toModuleAccessErrorMessage(err, {
              moduleLabel: 'Bóveda Digital',
              fallback: 'No se pudo descargar archivo.',
            }),
          });
        },
      });
  }

  private downloadFolder(row: RepositoryListItem): void {
    const zipArchive = this.currentZipPath();
    if (zipArchive) {
      // Dentro del ZIP: armar paquete OAIS completo (listZip), no GET /download (solo data).
      const zipDownloadMsg =
        'Dentro del empaquetado se descarga el archivo ZIP completo del paquete (Metadata, manifiesto y data).';
      this.vaultAppToast.show({
        type: 'info',
        title: 'Descarga',
        message: zipDownloadMsg,
      });
      this.loadingAction.set(true);
      this.buildFullOaisZipArchiveDownload$(zipArchive)
        .pipe(finalize(() => this.loadingAction.set(false)))
        .subscribe({
          next: (blob: Blob) => {
            const zipName =
              (zipArchive.split('/').filter(Boolean).pop() || 'paquete').replace(/\.zip$/i, '') +
              '.zip';
            this.triggerDownload(blob, zipName);
          },
          error: (err: unknown) => {
            this.vaultAppToast.show({
              type: 'error',
              title: 'Descarga',
              message: toModuleAccessErrorMessage(err, {
                moduleLabel: 'Bóveda Digital',
                fallback: 'No se pudo descargar el paquete ZIP.',
              }),
            });
          },
        });
      return;
    }

    const rowPath = this.resolveRepositoryDownloadPath(row);
    if (!rowPath || !rowPath.includes('/')) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Descarga',
        message: 'No se pudo resolver la ruta completa de la carpeta. Navegue hasta la carpeta e intente de nuevo.',
      });
      return;
    }

    const versionSuffix =
      row.version_number != null && row.version_number > 1
        ? `_v${row.version_number}`
        : '';

    // SIP/AIP/DIP: download-folder suele traer solo data/; armar paquete OAIS completo.
    const needsFullPackage =
      isSipFolderPath(rowPath) ||
      isSipFolderPath(this.currentPath()) ||
      isAipFolderPath(rowPath) ||
      isAipFolderPath(this.currentPath()) ||
      isUnderDipSegment(rowPath) ||
      isUnderDipSegment(this.currentPath());

    const packageRoot = needsFullPackage
      ? resolveOaisPackageRootPath(rowPath) || rowPath
      : rowPath;
    const packageLeaf = packageRoot.split('/').filter(Boolean).pop() || '';
    const baseName = (
      (needsFullPackage ? packageLeaf : '') ||
      row.logical_name ||
      row.name ||
      'folder'
    ).replace(/\.zip$/i, '');
    const downloadName = `${baseName}${versionSuffix}.zip`;

    this.loadingAction.set(true);
    const download$ = needsFullPackage
      ? this.collectOaisPackageFiles$(packageRoot).pipe(
          switchMap((paths) => {
            if (paths.length === 0) {
              return this.repositoryService.downloadFolder(packageRoot);
            }
            return this.repositoryService.downloadMultiple(paths).pipe(
              switchMap((blob) => from(rebaseZipToPackageRoot(blob, packageRoot))),
            );
          }),
        )
      : this.repositoryService.downloadFolder(rowPath);

    download$.pipe(finalize(() => this.loadingAction.set(false))).subscribe({
      next: (blob: Blob) => this.triggerDownload(blob, downloadName),
      error: (err: unknown) => {
        this.vaultAppToast.show({
          type: 'error',
          title: 'Descarga',
          message: toModuleAccessErrorMessage(err, {
            moduleLabel: 'Bóveda Digital',
            fallback: 'No se pudo descargar carpeta.',
          }),
        });
      },
    });
  }

  /**
   * AIP/DIP (.zip): mismo comportamiento que SIP.
   * Preferir la carpeta SIP hermana (mismo nombre de paquete) y el flujo
   * collectOaisPackageFiles$ + downloadMultiple + rebase.
   * Fallback: ZIP en disco (rebase si está completo) o reconstrucción listZip.
   */
  private buildFullOaisZipArchiveDownload$(zipPath: string): Observable<Blob> {
    const sipFolder = resolveSipFolderFromOaisZipPath(zipPath);
    if (sipFolder) {
      return this.collectOaisPackageFiles$(sipFolder).pipe(
        switchMap((paths) => {
          if (paths.length === 0) {
            return this.downloadOaisZipArchiveFallback$(zipPath);
          }
          return this.repositoryService.downloadMultiple(paths).pipe(
            switchMap((blob) => from(rebaseZipToPackageRoot(blob, sipFolder))),
          );
        }),
        catchError(() => this.downloadOaisZipArchiveFallback$(zipPath)),
      );
    }
    return this.downloadOaisZipArchiveFallback$(zipPath);
  }

  /** Fallback cuando no hay SIP hermano o falla el listado SIP. */
  private downloadOaisZipArchiveFallback$(zipPath: string): Observable<Blob> {
    const packageRoot = zipPath.replace(/\.zip$/i, '');
    return this.repositoryService.download(zipPath).pipe(
      switchMap((blob) =>
        from(zipBlobHasCompleteOaisLayout(blob)).pipe(
          switchMap((complete) => {
            if (complete) {
              return from(rebaseZipToPackageRoot(blob, packageRoot));
            }
            return this.rebuildOaisZipFromListZip$(zipPath);
          }),
        ),
      ),
      catchError(() => this.rebuildOaisZipFromListZip$(zipPath)),
    );
  }

  /** Fallback: reconstruir paquete OAIS pieza a pieza (cuando /download no trae el ZIP completo). */
  private rebuildOaisZipFromListZip$(zipPath: string): Observable<Blob> {
    return this.collectZipInternalFilePaths$(zipPath).pipe(
      switchMap((internals) => {
        if (internals.length === 0) {
          return this.repositoryService.download(zipPath);
        }
        return from(internals).pipe(
          concatMap((internalPath) =>
            this.repositoryService.downloadFromZip(zipPath, internalPath).pipe(
              map((blob) => ({ internalPath, blob })),
              catchError(() => of(null)),
            ),
          ),
          filter((part): part is { internalPath: string; blob: Blob } => part != null),
          toArray(),
          switchMap((parts) => {
            if (parts.length === 0) {
              return this.repositoryService.download(zipPath);
            }
            return from(packOaisZipInternalParts(parts, zipPath));
          }),
        );
      }),
      catchError(() => this.repositoryService.download(zipPath)),
    );
  }

  /** Recorre el contenido interno de un ZIP (incluye sondas OAIS si el API abre en data/). */
  private collectZipInternalFilePaths$(
    zipPath: string,
    internalPath = '',
    depth = 0,
  ): Observable<string[]> {
    if (depth > 14) {
      return of([]);
    }
    return this.repositoryService.listZip(zipPath, internalPath || undefined).pipe(
      switchMap((res) => {
        const items = this.normalizePackageItems(res);
        const files: string[] = [];
        const nested$: Observable<string[]>[] = [];

        for (const item of items) {
          // Como SIP: colgar del padre con el `name` del ítem (no el path plano del API).
          const full = resolveZipCollectItemPath(item, internalPath);
          if (!full) {
            continue;
          }
          const isDir = item.type === 'folder';
          if (isDir) {
            nested$.push(this.collectZipInternalFilePaths$(zipPath, full, depth + 1));
          } else if (
            depth === 0 &&
            !isOaisRootManifestFileName(item.name || full) &&
            !classifyOaisFolderName(full.split('/')[0] ?? '')
          ) {
            // Raíz ZIP: sueltos → bajo data/ (layout SIP).
            files.push(resolveZipCollectItemPath({ name: item.name || full }, 'data'));
          } else {
            files.push(full);
          }
        }

        // Raíz ZIP: completar metadata/data faltantes Y manifiestos (uno por paquete).
        const listedNames = items.map((item) => item.name || '');
        const listedHints = items.map((item) => ({ name: item.name, type: item.type }));
        const looksLikeContainer = listingLooksLikeOaisPackageContainer(listedHints);
        const missingFolderProbes =
          depth === 0 && !looksLikeContainer
            ? resolveMissingOaisSiblingProbes(listedNames)
            : [];
        const missingManifestFiles =
          depth === 0 && !looksLikeContainer
            ? resolveMissingOaisRootManifestFileProbes(listedNames)
            : [];
        const openedInsideData =
          depth === 0 &&
          !looksLikeContainer &&
          !listingHasOaisMetaOrManifiesto(listedNames);
        const nestedManifestFolders = resolveNestedOaisManifestProbeFolders(
          internalPath,
          listedHints,
        );

        const listed$ =
          nested$.length === 0
            ? of(files)
            : forkJoin(nested$).pipe(
                map((chunks) => files.concat(...chunks)),
              );

        const folderProbes$ =
          missingFolderProbes.length === 0
            ? of([] as string[])
            : forkJoin(
                missingFolderProbes.map((probe) =>
                  this.collectZipInternalFilePaths$(zipPath, probe, depth + 1).pipe(
                    catchError(() => of([])),
                  ),
                ),
              ).pipe(map((chunks) => chunks.flat()));

        const manifestProbe$ =
          missingManifestFiles.length === 0
            ? of(null as string | null)
            : this.probeZipInternalRootFile$(zipPath, missingManifestFiles);

        const nestedManifestProbe$ =
          nestedManifestFolders.length === 0
            ? of([] as string[])
            : forkJoin(
                nestedManifestFolders.map((folder) => {
                  const prefix = folder.replace(/\/+$/, '');
                  const probes = resolveMissingOaisRootManifestFileProbes([]).map((name) =>
                    prefix ? `${prefix}/${name}` : name,
                  );
                  return this.probeZipInternalRootFile$(zipPath, probes).pipe(
                    map((found) => (found ? [found] : [])),
                    catchError(() => of([] as string[])),
                  );
                }),
              ).pipe(map((chunks) => chunks.flat()));

        return forkJoin([listed$, folderProbes$, manifestProbe$, nestedManifestProbe$]).pipe(
          map(([listedPaths, probedFolders, manifestFile, nestedManifests]) => {
            const probedHasMeta = probedFolders.some((p) =>
              /^(metadata|metada|manifiesto|manifest|mets)(\/|$)/i.test(p),
            );
            let merged: string[];
            if (openedInsideData && probedHasMeta) {
              // API abrió como si estuviera en data/: prefijar data/ sin aplanar subcarpetas.
              const dataFromListing = listedPaths.map((p) =>
                /^data(\/|$)/i.test(p) ? p : `data/${p}`.replace(/\/+/g, '/'),
              );
              merged = [
                ...dataFromListing,
                ...probedFolders,
                ...(manifestFile ? [manifestFile] : []),
                ...nestedManifests,
              ];
            } else {
              merged = [
                ...listedPaths,
                ...probedFolders,
                ...(manifestFile ? [manifestFile] : []),
                ...nestedManifests,
              ];
            }
            return dedupeOaisPackagePaths(merged);
          }),
        );
      }),
      catchError(() => of([])),
    );
  }

  private isOaisPackageLocation(path: string): boolean {
    return (
      isSipFolderPath(path) ||
      isAipFolderPath(path) ||
      isUnderDipSegment(path)
    );
  }

  private resolveDownloadFileName(row: RepositoryListItem, forceZipExtension: boolean): string {
    const versionSuffix =
      row.version_number != null && row.version_number > 1
        ? `_v${row.version_number}`
        : '';
    let baseName = (row.logical_name?.trim() || row.name || 'archivo').replace(/\.zip$/i, '');
    if (forceZipExtension) {
      return `${baseName}${versionSuffix}.zip`;
    }
    const hasExt = /\.[a-z0-9]{2,8}$/i.test(baseName);
    return hasExt
      ? baseName.replace(/(\.[a-z0-9]+)$/i, `${versionSuffix}$1`)
      : `${baseName}${versionSuffix}`;
  }

  /**
   * Completa la raíz OAIS en la UI: metadata/ + data/ + manifest.xml (como SIP).
   */
  private resolveOaisZipRootListing$(
    zipPath: string,
    requestedInternal: string,
    res: unknown,
  ): Observable<RepositoryListItem[]> {
    const items = this.normalizeItems(res);
    const atRoot = !(sanitizeZipInternalPathForList(requestedInternal) ?? '');
    const isOaisZip =
      isUnderDipSegment(zipPath) || isAipFolderPath(zipPath) || isSipFolderPath(zipPath);
    if (!atRoot || !isOaisZip) {
      return of(items);
    }

    const listedNames = items.map((item) => item.name || '');
    const missingFolderProbes = resolveMissingOaisSiblingProbes(listedNames);
    const missingManifestFiles = resolveMissingOaisRootManifestFileProbes(listedNames);
    const uiProbeGroups = [
      {
        label: 'Metadata',
        probes: missingFolderProbes.filter((p) => /^(metadata|metada)$/i.test(p)),
      },
      {
        label: 'data',
        probes: missingFolderProbes.filter((p) => /^data$/i.test(p)),
      },
    ].filter((group) => group.probes.length > 0);

    if (uiProbeGroups.length === 0 && missingManifestFiles.length === 0) {
      return of(items);
    }

    const folderRows$ =
      uiProbeGroups.length === 0
        ? of([] as Array<RepositoryListItem | null>)
        : forkJoin(
            uiProbeGroups.map((group) =>
              this.probeZipInternalFolder$(zipPath, group.probes).pipe(
                map((found) =>
                  found
                    ? ({
                        name: found,
                        path: found,
                        type: 'folder',
                        extension: null,
                        size: null,
                        modified_at: null,
                        logical_name: null,
                        version_number: null,
                        is_latest: null,
                        internal_path: found,
                      } as RepositoryListItem)
                    : null,
                ),
              ),
            ),
          );

    const manifestRow$ =
      missingManifestFiles.length === 0
        ? of(null as RepositoryListItem | null)
        : this.probeZipInternalRootFile$(zipPath, missingManifestFiles).pipe(
            map((found) =>
              found
                ? ({
                    name: found,
                    path: found,
                    type: 'file',
                    extension: 'xml',
                    size: null,
                    modified_at: null,
                    logical_name: null,
                    version_number: null,
                    is_latest: null,
                    internal_path: found,
                  } as RepositoryListItem)
                : null,
            ),
          );

    return forkJoin([folderRows$, manifestRow$]).pipe(
      map(([folderRows, manifestRow]) => {
        const found = [
          ...folderRows.filter((row): row is RepositoryListItem => row !== null),
          ...(manifestRow ? [manifestRow] : []),
        ];
        return mergeOaisListingItems(items, found);
      }),
    );
  }

  private probeZipInternalFolder$(
    zipPath: string,
    probes: string[],
  ): Observable<string | null> {
    return from(probes).pipe(
      concatMap((probe) =>
        this.repositoryService.listZip(zipPath, probe).pipe(
          map((res) => {
            const items = this.normalizeItems(res).filter(
              (it) => !this.isZipNavigationEntry(it.name),
            );
            // Solo aceptar la sonda si hay contenido real (evita Metadata vacía
            // cuando el API responde 200 a "Metadata" pero los archivos están en "metadata").
            return items.length > 0 ? probe : null;
          }),
          catchError(() => of(null as string | null)),
        ),
      ),
      first((found): found is string => found != null, null),
    );
  }

  /** Confirma manifest.xml (u otra variante) dentro del ZIP. */
  private probeZipInternalRootFile$(
    zipPath: string,
    probes: string[],
  ): Observable<string | null> {
    return from(probes).pipe(
      concatMap((probe) =>
        this.repositoryService.downloadFromZip(zipPath, probe).pipe(
          map(() => probe),
          catchError(() => of(null as string | null)),
        ),
      ),
      first((found): found is string => found != null, null),
    );
  }

  /**
   * Recorre la carpeta OAIS: metadata/ + data/ + manifest.xml por cada expediente.
   */
  private collectOaisPackageFiles$(packageRoot: string): Observable<string[]> {
    const root = packageRoot.replace(/\/+$/, '');
    return this.repositoryService.list(root).pipe(
      switchMap((res) => {
        const items = this.normalizePackageItems(res);
        const listedNames = items.map((item) => item.name || '');
        const listedHints = items.map((item) => ({ name: item.name, type: item.type }));
        const isContainer = listingLooksLikeOaisPackageContainer(listedHints);
        const folderProbes = isContainer ? [] : resolveMissingOaisSiblingProbes(listedNames);
        const fileProbes = isContainer
          ? []
          : resolveMissingOaisRootManifestFileProbes(listedNames);
        const nestedManifestFolders = isContainer
          ? resolveNestedOaisManifestProbeFolders(root, listedHints)
          : [];
        const nested$: Observable<string[]>[] = [
          this.collectRepositoryFilesUnder$(root),
          ...folderProbes.map((name) =>
            this.collectRepositoryFilesUnder$(`${root}/${name}`).pipe(catchError(() => of([]))),
          ),
        ];
        const manifest$ =
          fileProbes.length === 0
            ? of([] as string[])
            : this.probeFirstRepositoryRootManifest$(root, fileProbes);
        const nestedManifest$ =
          nestedManifestFolders.length === 0
            ? of([] as string[])
            : forkJoin(
                nestedManifestFolders.map((folder) =>
                  this.probeFirstRepositoryRootManifest$(
                    folder,
                    resolveMissingOaisRootManifestFileProbes([]),
                  ).pipe(catchError(() => of([] as string[]))),
                ),
              ).pipe(map((chunks) => chunks.flat()));
        return forkJoin([forkJoin(nested$), manifest$, nestedManifest$]).pipe(
          map(([chunks, manifestPaths, nestedManifests]) =>
            dedupeOaisPackagePaths([...chunks.flat(), ...manifestPaths, ...nestedManifests]),
          ),
        );
      }),
      catchError(() => {
        const probes = allOaisFolderProbeNames();
        const nested$ = [
          this.collectRepositoryFilesUnder$(root),
          ...probes.map((name) =>
            this.collectRepositoryFilesUnder$(`${root}/${name}`).pipe(catchError(() => of([]))),
          ),
        ];
        const manifest$ = this.probeFirstRepositoryRootManifest$(
          root,
          resolveMissingOaisRootManifestFileProbes([]),
        );
        return forkJoin([forkJoin(nested$), manifest$]).pipe(
          map(([chunks, manifestPaths]) =>
            dedupeOaisPackagePaths([...chunks.flat(), ...manifestPaths]),
          ),
        );
      }),
    );
  }

  /** Solo el primer manifesto raíz que exista (evita manifest.xml + Manifest.xml). */
  private probeFirstRepositoryRootManifest$(
    packageRoot: string,
    fileNames: string[],
  ): Observable<string[]> {
    return from(fileNames).pipe(
      concatMap((name) => {
        const path = `${packageRoot.replace(/\/+$/, '')}/${name}`;
        return this.repositoryService.download(path).pipe(
          map(() => path),
          catchError(() => of(null as string | null)),
        );
      }),
      first((found): found is string => found != null, null),
      map((found) => (found ? [found] : [])),
    );
  }

  /**
   * Recorre la carpeta e incluye metadata, manifiesto y data.
   * No omite meta.json / checksum: forman parte del empaquetado.
   */
  private collectRepositoryFilesUnder$(
    folderPath: string,
    depth = 0,
  ): Observable<string[]> {
    if (depth > 14) {
      return of([]);
    }
    return this.repositoryService.list(folderPath).pipe(
      switchMap((res) => {
        const items = this.normalizePackageItems(res);
        const files: string[] = [];
        const nested$: Observable<string[]>[] = [];
        for (const item of items) {
          const full = resolveRepositoryItemPath(
            item.path || item.name || '',
            folderPath,
          );
          if (!full) {
            continue;
          }
          if (item.type === 'folder') {
            nested$.push(this.collectRepositoryFilesUnder$(full, depth + 1));
          } else {
            files.push(full);
          }
        }
        const listedNames = items.map((item) => item.name || '');
        if (shouldProbeOaisRootManifest(listedNames)) {
          nested$.push(
            this.probeFirstRepositoryRootManifest$(
              folderPath,
              resolveMissingOaisRootManifestFileProbes(listedNames),
            ).pipe(catchError(() => of([]))),
          );
        }
        if (nested$.length === 0) {
          return of(files);
        }
        return forkJoin(nested$).pipe(
          map((chunks) => files.concat(...chunks)),
        );
      }),
      catchError(() => of([])),
    );
  }

  /** Listado para empaquetado: no oculta meta.json ni checksum. */
  private normalizePackageItems(raw: unknown): RepositoryListItem[] {
    if (!raw || typeof raw !== 'object') return [];
    const record = raw as Record<string, unknown>;
    let list: unknown[] = [];
    if (Array.isArray(record['items'])) list = record['items'] as unknown[];
    else if (Array.isArray(record['results'])) list = record['results'] as unknown[];
    else if (Array.isArray(record['files'])) list = record['files'] as unknown[];
    else if (Array.isArray(raw)) list = raw as unknown[];

    return list
      .map((it) => this.normalizeItem(it))
      .filter((it): it is RepositoryListItem => it !== null)
      .filter((it) => !this.isZipNavigationEntry(it.name));
  }

  private downloadSelected(): void {
    const paths = this.selectedFiles();
    if (paths.length === 0) return;
    this.loadingAction.set(true);
    this.repositoryService
      .downloadMultiple(paths)
      .pipe(finalize(() => this.loadingAction.set(false)))
      .subscribe({
        next: (blob: Blob) => this.triggerDownload(blob, 'repository-files.zip'),
        error: (err: unknown) => {
          this.vaultAppToast.show({
            type: 'error',
            title: 'Descarga',
            message: toModuleAccessErrorMessage(err, {
              moduleLabel: 'Bóveda Digital',
              fallback: 'No se pudo descargar ZIP.',
            }),
          });
        },
      });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private repositoryFileTypeLabel(type: string): string {
    return this.fileTypeOptions.find((option) => option.value === type)?.label ?? type;
  }

  private normalizeItems(raw: unknown): RepositoryListItem[] {
    if (!raw || typeof raw !== 'object') return [];
    const record = raw as Record<string, unknown>;
    let list: unknown[] = [];
    if (Array.isArray(record['items'])) list = record['items'] as unknown[];
    else if (Array.isArray(record['results'])) list = record['results'] as unknown[];
    else if (Array.isArray(record['files'])) list = record['files'] as unknown[];
    else if (Array.isArray(raw)) list = raw as unknown[];

    const items = list
      .map((it) => this.normalizeItem(it))
      .filter((it): it is RepositoryListItem => it !== null)
      .filter((it) => !this.isZipNavigationEntry(it.name));

    if (!this.repositorySearchActive()) {
      return items;
    }
    return items.filter((it) => !this.isTechnicalRepositoryFile(it.name));
  }

  /** Entradas de navegación del listado interno de ZIP (no son contenido real). */
  private isZipNavigationEntry(name: string): boolean {
    const n = (name ?? '').trim();
    return n === '.' || n === '..' || n === '...';
  }

  /** Metadatos técnicos de paquete SIP: solo ocultar en búsqueda global (ENTREGA 1.0.1 / #0000033). */
  private isTechnicalRepositoryFile(name: string): boolean {
    const n = (name ?? '').trim().toLowerCase();
    if (!n) return true;
    if (n.endsWith('_meta.json') || n.endsWith('.meta.json')) {
      return true;
    }
    return n === 'meta.json' || n.endsWith('.checksum.json');
  }

  private normalizeItem(raw: unknown): RepositoryListItem | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const name = String(r['name'] ?? r['filename'] ?? '');
    const zipPathRaw =
      typeof r['zip_path'] === 'string'
        ? (r['zip_path'] as string)
        : typeof r['zipPath'] === 'string'
          ? (r['zipPath'] as string)
          : '';
    const zipInternalRaw =
      typeof r['zip_internal_path'] === 'string'
        ? (r['zip_internal_path'] as string)
        : typeof r['zip_internal'] === 'string'
          ? (r['zip_internal'] as string)
          : typeof r['internal_path'] === 'string'
            ? (r['internal_path'] as string)
            : '';
    const pathFromApi = String(r['path'] ?? r['file_path'] ?? r['full_path'] ?? '');
    const path =
      resolveRepositorySearchHitPath({
        path: pathFromApi,
        name,
        zip_path: zipPathRaw || null,
        zip_internal_path: zipInternalRaw || null,
        internal_path:
          typeof r['internal_path'] === 'string' ? (r['internal_path'] as string) : null,
      }) ||
      pathFromApi ||
      name;
    const type = String(r['type'] ?? (r['is_directory'] ? 'folder' : 'file'));
    const logicalNameRaw = r['logical_name'];
    const logical_name =
      typeof logicalNameRaw === 'string' && logicalNameRaw.trim()
        ? logicalNameRaw.trim()
        : null;
    const versionRaw = r['version_number'];
    let version_number: number | null = null;
    if (typeof versionRaw === 'number' && Number.isFinite(versionRaw)) {
      version_number = versionRaw;
    } else if (typeof versionRaw === 'string' && versionRaw.trim() !== '') {
      const parsedVersion = Number(versionRaw);
      version_number = Number.isFinite(parsedVersion) ? parsedVersion : null;
    }
    const is_latest =
      typeof r['is_latest'] === 'boolean'
        ? r['is_latest']
        : r['is_latest'] === 'true' || r['is_latest'] === 1
          ? true
          : r['is_latest'] === 'false' || r['is_latest'] === 0
            ? false
            : null;
    const searchZipHit = splitZipVirtualAssignmentPath(path);
    const internalFromHit = searchZipHit?.internalPath || null;
    return {
      ...(r as RepositoryListItem),
      name,
      path: path || name,
      type: type === 'folder' || type === 'directory' ? 'folder' : 'file',
      size: typeof r['size'] === 'number' ? (r['size'] as number) : null,
      modified_at: this.getModifiedDate(r),
      extension: typeof r['extension'] === 'string' ? (r['extension'] as string) : null,
      zip_path: zipPathRaw.trim() || searchZipHit?.zipPath || undefined,
      zip_internal_path:
        normalizeZipInternalPath(zipInternalRaw) || internalFromHit || undefined,
      internal_path:
        internalFromHit ||
        (typeof r['internal_path'] === 'string' && (r['internal_path'] as string).trim()
          ? extractZipInternalRelativePath(r['internal_path'] as string) ||
            (r['internal_path'] as string).trim()
          : extractZipInternalRelativePath(path || name) || null),
      logical_name,
      version_number,
      is_latest,
    };
  }

  displayRepositoryItemName(row: RepositoryListItem): string {
    return row.logical_name?.trim() || row.name;
  }

  /** Nombre con el sufijo de versión (` vN`) cuando aplica (v2, v3, ...). */
  displayRepositoryItemNameWithVersion(row: RepositoryListItem): string {
    const base = this.displayRepositoryItemName(row);
    const version = row.version_number;
    return version != null && version > 1 ? `${base} v${version}` : base;
  }

  formatRepositoryVersion(row: RepositoryListItem): string {
    if (row.version_number == null || row.version_number <= 1) {
      return '—';
    }
    return `v${row.version_number}`;
  }

  repositoryItemIsLatest(row: RepositoryListItem): boolean {
    return row.is_latest === true && row.version_number != null;
  }

  private getModifiedDate(r: Record<string, unknown>): string | null {
    const candidates = [
      r['modified_at'],
      r['modifiedAt'],
      r['modified'],
      r['last_modified'],
      r['lastModified'],
      r['updated_at'],
      r['updatedAt'],
      r['created_at'],
      r['createdAt'],
      r['date'],
    ];
    const rawDate = candidates.find((v) => v !== null && v !== undefined);
    return this.normalizeDateValue(rawDate);
  }

  private normalizeDateValue(raw: unknown): string | null {
    if (raw === null || raw === undefined) return null;

    // ISO/date string already provided by backend
    if (typeof raw === 'string' && Number.isNaN(Number(raw))) {
      const parsed = new Date(raw);
      if (Number.isNaN(parsed.getTime())) return raw;
      return this.toDateOnly(parsed);
    }

    // Numeric timestamps (seconds, milliseconds, or fractional seconds)
    const numeric = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(numeric)) return null;

    // Heuristic: values < 1e12 are likely seconds, otherwise milliseconds
    const ms = numeric < 1e12 ? numeric * 1000 : numeric;
    const parsed = new Date(ms);
    return Number.isNaN(parsed.getTime()) ? null : this.toDateOnly(parsed);
  }

  private toDateOnly(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  isFolder(row: RepositoryListItem): boolean {
    return row.type === 'folder';
  }

  isZipRow(row: RepositoryListItem): boolean {
    if (this.isFolder(row)) {
      return false;
    }
    const rowPath = (row.path || row.name || '').trim();
    const ext = (row.extension || '').toLowerCase();
    return ext === 'zip' || rowPath.toLowerCase().endsWith('.zip');
  }

  private isZipAssignPath(path: string): boolean {
    return (path ?? '').trim().toLowerCase().endsWith('.zip');
  }

  getPath(row: RepositoryListItem): string {
    if (this.currentZipPath()) {
      return resolveZipInternalRowSegment(row);
    }
    return row.path || '';
  }

  /** Ruta absoluta OAIS del ítem en el listado actual (para filtro, navegación y asignación). */
  private getFullRepositoryRowPath(row: RepositoryListItem): string {
    const raw = this.getPath(row) || row.name || '';
    if (!raw) {
      return '';
    }
    if (this.currentZipPath()) {
      return resolveZipInternalRowSegment(row);
    }
    return resolveRepositoryItemPath(raw, this.currentPath());
  }

  /** Ruta completa solo para API download/preview; no altera el árbol del explorador. */
  private resolveRepositoryDownloadPath(row: RepositoryListItem): string {
    if (this.currentZipPath()) {
      return resolveZipInternalRowSegment(row);
    }
    const zipHit = this.resolveZipSearchHit(row);
    if (zipHit) {
      return zipHit.internalPath;
    }
    const raw = this.getPath(row) || row.name || '';
    if (!raw) {
      return '';
    }
    const resolved = resolveRepositoryItemPath(raw, this.currentPath());
    if (resolved.includes('/')) {
      return resolved;
    }
    const base = normalizeRepositoryExplorerPath(this.currentPath());
    if (base && base !== EXPLORER_ROOT_PATH) {
      return `${base.replace(/\/+$/, '')}/${resolved}`.replace(/\/+/g, '/');
    }
    return resolved;
  }

  /**
   * Hit de búsqueda dentro de ZIP (`zip_path` + interno, o path `…zip::…`).
   * Fuera de un ZIP abierto, download/preview deben usar `/zip/download`.
   */
  private resolveZipSearchHit(
    row: RepositoryListItem,
  ): { zipPath: string; internalPath: string } | null {
    const zipField = String(row['zip_path'] ?? '').trim();
    const internalField = normalizeZipInternalPath(
      String(row['zip_internal_path'] ?? row.internal_path ?? '').trim(),
    );
    if (zipField && internalField) {
      return {
        zipPath: normalizeRepositoryExplorerPath(zipField.replace(/\\/g, '/')),
        internalPath: internalField,
      };
    }
    const fromPath = splitZipVirtualAssignmentPath(this.getPath(row) || row.path || '');
    if (fromPath?.zipPath && fromPath.internalPath) {
      return fromPath;
    }
    if (zipField) {
      const inner =
        internalField ||
        extractZipInternalRelativePath(row.internal_path) ||
        normalizeZipInternalPath(row.name);
      if (inner) {
        return {
          zipPath: normalizeRepositoryExplorerPath(zipField.replace(/\\/g, '/')),
          internalPath: inner,
        };
      }
    }
    return null;
  }

  private isRowUnderAip(row: RepositoryListItem): boolean {
    const full = this.getFullRepositoryRowPath(row) || this.getPath(row) || '';
    return isAipFolderPath(full) || isAipFolderPath(this.currentPath());
  }

  displayRepositoryItemType(row: RepositoryListItem): string {
    if (this.isFolder(row)) {
      return 'Carpeta';
    }
    if (this.isZipRow(row)) {
      return 'ZIP';
    }
    const ext = (row.extension || row.name.split('.').pop() || '').toLowerCase();
    const labels: Record<string, string> = {
      pdf: 'PDF',
      png: 'Imagen',
      jpg: 'Imagen',
      jpeg: 'Imagen',
      gif: 'Imagen',
      tif: 'Imagen',
      tiff: 'Imagen',
      webp: 'Imagen',
      txt: 'Texto',
      csv: 'Texto',
      xml: 'XML',
      json: 'JSON',
      doc: 'Documento',
      docx: 'Documento',
      xls: 'Hoja de cálculo',
      xlsx: 'Hoja de cálculo',
    };
    return labels[ext] || (ext ? ext.toUpperCase() : 'Archivo');
  }

  displayRepositoryItemSize(row: RepositoryListItem): string {
    if (this.isFolder(row)) {
      if (row.size == null || row.size <= 0) {
        return '—';
      }
    }
    if (row.size == null || !Number.isFinite(row.size)) {
      return '—';
    }
    return this.formatRepositoryBytes(row.size);
  }

  private formatRepositoryBytes(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = -1;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    const rounded = value >= 10 || unitIndex < 0 ? value.toFixed(0) : value.toFixed(1);
    return unitIndex < 0 ? `${bytes} B` : `${rounded} ${units[unitIndex]}`;
  }

  downloadPreviewFile(): void {
    const fileName = this.previewDownloadName().trim();
    const url = this.previewSrcUrl();
    if (!fileName || !url || !url.startsWith('blob:')) {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Descarga',
        message: 'No hay archivo de vista previa para descargar.',
      });
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  }

  private buildExplorerFileTree(): FileNode[] {
    const items = this.rows();
    const current = this.currentPath().trim();
    if (items.length === 0 && !current) {
      return [];
    }
    if (this.repositorySearchActive()) {
      return this.buildExplorerSearchResultTree(items);
    }
    const cache = this.explorerChildCache();
    const exp = this.explorerExpandedPaths();
    const topNodes = items.map((row) => this.buildExplorerFileNodeForRow(row, cache, exp));
    return [
      {
        name: this.explorerContextLabel(),
        path: EXPLORER_ROOT_PATH,
        type: 'directory',
        expanded: true,
        children: topNodes,
      },
    ];
  }

  /** Lista plana de archivos bajo un nodo raíz (el panel derecho usa hijos directos tipo file). */
  private buildExplorerSearchResultTree(items: RepositoryListItem[]): FileNode[] {
    const fileNodes: FileNode[] = [];
    for (const row of items) {
      if (this.isFolder(row)) {
        continue;
      }
      const path = this.getPath(row) || row.name;
      fileNodes.push({
        name: row.name,
        path,
        type: 'file',
        size: row.size ?? undefined,
        extension: row.extension ?? undefined,
      });
    }
    return fileNodes;
  }

  private syncExplorerSelectionAfterSearch(_rows: RepositoryListItem[]): void {
    this.selectedExplorerPath.set(null);
    this.repoStore.setSelectedExplorerPath(null);
  }

  private buildExplorerFileNodeForRow(
    row: RepositoryListItem,
    cache: Record<string, RepositoryListItem[]>,
    exp: Set<string>,
  ): FileNode {
    const p = this.getPath(row) || row.name;
    if (!this.isFolder(row)) {
      return {
        name: row.name,
        path: p,
        type: 'file',
        size: row.size ?? undefined,
        extension: row.extension ?? undefined,
      };
    }
    const sub = cache[p];
    if (sub !== undefined) {
      return {
        name: row.name,
        path: p,
        type: 'directory',
        expanded: exp.has(p),
        children: sub.map((r) => this.buildExplorerFileNodeForRow(r, cache, exp)),
      };
    }
    return {
      name: row.name,
      path: p,
      type: 'directory',
      children: [],
      childrenLazy: true,
      expanded: exp.has(p),
    };
  }

  private loadExplorerFolderChildren(
    path: string,
    options?: { rehydrate?: boolean; onRehydrateSettled?: (failed: boolean) => void },
  ): void {
    const apiPath = normalizeRepositoryExplorerPath(path);
    if (!apiPath || apiPath === REPOSITORY_EXPLORER_ROOT_PATH) {
      options?.onRehydrateSettled?.(false);
      return;
    }
    if (this.explorerListInFlight.has(apiPath) || this.explorerChildCache()[apiPath] !== undefined) {
      options?.onRehydrateSettled?.(false);
      return;
    }
    this.explorerListInFlight.add(apiPath);
    const currentZip = this.currentZipPath();
    const req = currentZip
      ? this.repositoryService.listZip(currentZip, apiPath || undefined)
      : this.repositoryService.list(apiPath, undefined);
    req
      .pipe(
        timeout(45000),
        finalize(() => this.explorerListInFlight.delete(apiPath)),
      )
      .subscribe({
        next: (res: unknown) => {
          const items = this.applyAssignmentScopeFilter(this.normalizeItems(res), apiPath);
          this.explorerChildCache.update((c) => ({ ...c, [apiPath]: items }));
          this.repoStore.setExplorerChildren(apiPath, items);
          options?.onRehydrateSettled?.(false);
        },
        error: (err: unknown) => {
          this.collapseExplorerExpandedPath(apiPath, path);
          if (options?.rehydrate) {
            options.onRehydrateSettled?.(true);
            return;
          }
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Bóveda Digital',
              fallback: 'No se pudo listar el contenido de la subcarpeta.',
            }),
          );
          options?.onRehydrateSettled?.(true);
        },
      });
  }

  private collapseExplorerExpandedPath(apiPath: string, rawPath?: string): void {
    this.updateExplorerExpandedPaths((current) => {
      const next = new Set(current);
      next.delete(apiPath);
      if (rawPath) {
        next.delete(rawPath);
        next.delete(normalizeRepositoryExplorerPath(rawPath));
      }
      return next;
    });
  }

  private rehydrateExpandedExplorer(): void {
    const persisted = Array.from(this.explorerExpandedPaths())
      .map((path) => normalizeRepositoryExplorerPath(path))
      .filter((path) => path && path !== REPOSITORY_EXPLORER_ROOT_PATH);
    if (persisted.length === 0) {
      return;
    }

    let pending = persisted.length;
    let pruned = 0;

    const onRehydrateSettled = (failed: boolean): void => {
      if (failed) {
        pruned += 1;
      }
      pending -= 1;
      if (pending > 0) {
        return;
      }
      if (pruned > 0) {
        this.info.set(
          'Algunas carpetas del árbol ya no están disponibles en el servidor; se cerraron automáticamente.',
        );
      }
    };

    for (const folderPath of persisted) {
      this.loadExplorerFolderChildren(folderPath, { rehydrate: true, onRehydrateSettled });
    }
  }

  private explorerContextLabel(): string {
    if (this.currentZipPath()) {
      const z = this.currentZipPath() ?? '';
      const inner = this.zipInternalPath() || 'raíz';
      const base = z.includes('/') ? z.split('/').filter(Boolean).pop() : z;
      return `ZIP · ${base || z} / ${inner}`;
    }
    const p = this.currentPath();
    if (!p) {
      return 'Contenido (raíz)';
    }
    const parts = p.split('/').filter(Boolean);
    return parts.length ? `Contenido: ${parts[parts.length - 1]}` : 'Contenido (raíz)';
  }

}
